import { examenAlCierre, compactarFiebreRepetida, ordenarNarrativaNeurologica, esLineaRiesgosParaSeguridad } from './presentacionEvolucion';
import { identificarNotaRiesgo } from './riesgosRecibidos';
import {
  compilarSistemaDetalle, sistemaTieneDetalle, CAMPOS_POR_SISTEMA, cumpleDependencia, ecosEntreSistemas,
  camposVisibles, campoConValor, escalasDe, escalaConValores, valorNormalDeCampo,
  valorNormalDeEscala, scoreEscala,
} from './escalasEvolucion';
import { detectPii, detectPiiDuro } from './piiDetect';
import { ordenarCefaloCaudal } from './ordenCefaloCaudal';
import { catalogoYaNarradoEnNota } from './ecosCatalogo';

// Evolución por sistema — dominio puro de MiPAE+.
//
// "Registro del día → evolución por sistema": el usuario marca, por sistema,
// hallazgos rápidos y detalle estructurado, y puede agregar una nota de
// evolución (texto libre) con el detalle clínico. Arma una evolución de
// enfermería ordenada por aparato, lista para copiar.
//
// Privacidad: las notas son de texto libre — la UI recuerda no escribir nombres
// ni RUT. Nada se persiste (módulo efímero).

// Catálogo de sistemas propio de la evolución (más completo que el SBAR de
// entrega). No se reutiliza el de entregaTurno para no acoplar ambos flujos.
export const SISTEMAS = [
  {
    id: 'neuro', label: 'Neurológico',
    // El estado de conciencia, la sedación y el dolor se capturan estructurados
    // en "Escalas y signos"; aquí quedan los hallazgos rápidos que no lo están.
    opciones: ['Agitación psicomotora', 'Déficit motor', 'Déficit motor nuevo', 'Convulsiones', 'Drenaje ventricular (DVE)', 'Contención mecánica'],
  },
  {
    id: 'hemodinamia', label: 'Hemodinamia',
    // Presión, FC, ritmo, temperatura, DVA por droga, perfusión y edema se
    // capturan estructurados. "Shock" se retiró a pedido del fundador
    // (2026-07-16): la etiqueta sola alarmaba sin dato; el cuadro se lee en
    // presión + drogas + perfusión. Un draft viejo con ese hallazgo no rompe
    // (buildEvolucion filtra contra `opciones` y desaparece en silencio).
    opciones: ['Arritmia', 'Marcapasos'],
  },
  {
    id: 'ventilacion', label: 'Ventilación',
    // El soporte (O₂/CNAF/VNI/VMI), secreciones y gases se capturan estructurados.
    opciones: ['TOT', 'Traqueostomía', 'Prono', 'Extubación reciente'],
  },
  {
    id: 'gastro', label: 'Gastro-Metabólico',
    // Régimen, tolerancia, glicemia/insulina y deposiciones se capturan
    // estructurados; estos chips son los cuadros mayores que no caben en un
    // campo (falla hepática y pancreatitis a pedido del fundador, 2026-07-16).
    opciones: ['Náuseas o vómitos', 'Distensión abdominal', 'Ictericia', 'Falla hepática', 'Pancreatitis'],
  },
  {
    id: 'renal', label: 'Renal',
    // Vía, ritmo diurético, orina, función renal y HE se capturan estructurados.
    opciones: ['Globo vesical', 'Retención urinaria'],
  },
  {
    id: 'infecto', label: 'Infectología',
    // Sin hallazgos rápidos (norma 2026-07-16: "se describe todo arriba"):
    // estado febril, parámetros con valor, cultivos con estado/fecha,
    // aislamiento y ATB se capturan TODOS estructurados. Drafts viejos con
    // los chips retirados no rompen (buildEvolucion filtra contra opciones).
    opciones: [],
  },
  {
    id: 'hemato', label: 'Hematología',
    // Sin hallazgos rápidos (dictado del fundador, 2026-07-16: "hallazgos
    // molesta, se describe todo arriba"): tromboprofilaxis, sangrado, anemia,
    // laboratorio y transfusión se capturan TODOS estructurados. Un draft
    // viejo con los chips retirados no rompe (buildEvolucion filtra contra
    // `opciones`).
    opciones: [],
  },
  {
    id: 'tegumentario', label: 'Tegumentario · piel y heridas',
    heridas: true, // segmento especial: heridas de piel y lesiones por presión
    // Sin hallazgos rápidos (norma 2026-07-16): la dehiscencia y los signos
    // inflamatorios viven en los campos de la herida operatoria (herBordes,
    // herSecrecion), la LPP/DAI en su rama, y los drenajes por dispositivo en
    // Invasivos. Drafts viejos con chips retirados no rompen (buildEvolucion
    // filtra contra `opciones`).
    opciones: [],
  },
  {
    id: 'invasivos', label: 'Invasivos',
    // Los hallazgos rápidos se retiraron: duplicaban el flujo por dispositivo
    // (tap-to-add + tarjeta) y solo metían nombres pelados al texto sin calibre,
    // ubicación ni estado. Todo el detalle de invasivos vive por dispositivo.
    opciones: [],
  },
  {
    // Segmento de cierre B2B: las intervenciones del turno que no son de un
    // aparato corporal (muestras no-cultivo, traslados, "Otro"…) encuentran aquí
    // su lugar, para que la evolución registre TODO lo que el enfermero hizo.
    id: 'procedimientos', label: 'Procedimientos e intervenciones',
    opciones: ['Toma de muestras', 'Curación', 'Aspiración de secreciones', 'Instalación de acceso', 'Retiro de dispositivo', 'Traslado', 'Examen / imagen', 'Procedimiento al lado de la cama'],
  },
];

// Turno al que corresponde la evolución (encabezado, no es PII).
export const TURNOS = ['Diurno', 'Tarde', 'Noche', 'Largo'];

// ── Apertura: estado general y condición ─────────────────────────────────────
// Cómo está el paciente HOY, en dos toques (feedback del fundador, 2026-07-14):
// la evolución real abre con "Paciente en buen estado general, en condición
// estable." antes de entrar por sistema. Catálogo cerrado (PII-safe por
// construcción); la prosa vive junto a la opción para que la UI muestre lo que
// se firma ANTES de marcar (misma transparencia que las basales).
export const ESTADO_GENERAL = [
  { v: 'bueno',   label: 'Bueno',   prosa: 'en buen estado general' },
  { v: 'regular', label: 'Regular', prosa: 'en regular estado general' },
  { v: 'malo',    label: 'Malo',    prosa: 'en mal estado general' },
];
export const CONDICION = [
  { v: 'estable',   label: 'Estable',               prosa: 'en condición estable' },
  { v: 'cuidado',   label: 'De cuidado',            prosa: 'en condición de cuidado' },
  { v: 'grave',     label: 'Grave',                 prosa: 'en condición grave' },
  { v: 'muy-grave', label: 'Muy grave',             prosa: 'en condición muy grave' },
  { v: 'extrema',   label: 'Extremadamente grave',  prosa: 'en condición extremadamente grave' },
];
// Urgencia para el color de lo seleccionado (tres niveles, lib/tonoClinico.js;
// misma lectura que TONO_GRAVEDAD de la Recepción). Un test exige que cubran
// sus catálogos enteros.
export const TONO_ESTADO_GENERAL = { bueno: 'estable', regular: 'atencion', malo: 'critico' };
export const TONO_CONDICION = { estable: 'estable', cuidado: 'atencion', grave: 'critico', 'muy-grave': 'critico', extrema: 'critico' };

// Oración de apertura de la evolución (o '' si no se marcó nada). Tolera tener
// solo uno de los dos: "Paciente en condición grave." también es apertura.
// Sin palabras con género — no necesita generizar.
export function aperturaEvolucion(state) {
  const eg = ESTADO_GENERAL.find((o) => o.v === state?.estadoGeneral);
  const c = CONDICION.find((o) => o.v === state?.condicion);
  if (!eg && !c) return '';
  return `Paciente ${[eg?.prosa, c?.prosa].filter(Boolean).join(', ')}.`;
}

// Sexo del paciente — solo para la concordancia de género del texto (no es PII).
export const SEXOS = [
  { v: 'F', label: 'Femenino' },
  { v: 'M', label: 'Masculino' },
];

// ── Invasivos por dispositivo ────────────────────────────────────────────────
// La sección "Invasivos" se arma como una lista de dispositivos, cada uno con su
// calibre (#), ubicación, día de instalación, permeabilidad/reflujo, técnica de
// permeabilización y signos de flebitis/infección. Antes era un checklist plano
// ("VVP, CVC, TOT… funcionales"); ahora cada acceso se describe como en una
// nota de enfermería real (ej: "VVP #20 BD, día 1; infunde sin resistencia,
// técnica push-stop con SF; sin signos de flebitis ni infección.").
// EN ORDEN CÉFALO-CAUDAL (dictado del fundador, 2026-07-16): así se recorren
// al examinar y así se ordenan al imprimir (compilarDispositivos ordena por
// este índice: SNG antes que TOT, CVC antes que la línea arterial, el CUP al
// final). Nuevos: DVE, Swan-Ganz y PiCCO; "CUP / Foley" pasó a llamarse
// catéter urinario permanente (mismo rename que la vía de diuresis en renal).
// El orden ES céfalo-caudal y `compilarDispositivos` imprime por este índice.
// Los tres de 2026-09-21 (encargo del fundador) entraron cada uno en su altura,
// no al final: la sonda nasoyeyunal con las otras sondas nasales, la
// gastrostomía en el abdomen después del marcapasos, y el Flexiseal al final,
// que es lo más distal del tubo digestivo.
export const DISPOSITIVOS_TIPO = [
  'DVE', 'SNG', 'SOG', 'Sonda nasoyeyunal', 'TOT', 'Traqueostomía',
  'Catéter venoso central', 'Swan-Ganz', 'PiCCO', 'PICC',
  'Línea arterial', 'VVP', 'Nexiva', 'Catéter de hemodiálisis', 'Marcapasos',
  'Gastrostomía', 'Drenaje', 'Catéter urinario permanente', 'Flexiseal',
];

// Accesos vasculares: son los únicos donde permeabilidad/reflujo, técnica
// push-stop y flebitis tienen sentido clínico. El resto (vía aérea, sondas,
// drenajes, marcapasos) no maneja esos conceptos — se les oculta en la UI.
const VASCULARES = new Set(['VVP', 'Nexiva', 'Catéter venoso central', 'PICC', 'Línea arterial', 'Catéter de hemodiálisis', 'Swan-Ganz', 'PiCCO']);

// Nombres antiguos que siguen vivos en registros ya escritos. El rename de
// CVC a "Catéter venoso central" (dictado del fundador, 27-jul) sigue el
// mismo camino que el de "catéter urinario permanente": el catálogo cambia y
// el valor viejo se normaliza al leer, jamás se migran datos.
// El Flexiseal es la EXCEPCIÓN al criterio del rename de Foley: ahí el nombre
// clínico genérico ganó, aquí el fundador lo miró escrito y decidió al revés
// (2026-09-21, "estoy de acuerdo con el sistema de manejo fecal, pero está
// feo"). La marca ES la palabra con que se pide y con que se habla en la
// unidad, y el catálogo se escribe en el idioma de quien registra. El nombre
// genérico queda de alias, junto con la grafía de la marca, para que las tres
// formas resuelvan al mismo valor.
export const ALIAS_DISPOSITIVO = {
  CVC: 'Catéter venoso central',
  'CUP / Foley': 'Catéter urinario permanente',
  'Flexi-Seal': 'Flexiseal',
  'Sistema de manejo fecal': 'Flexiseal',
};
export function tipoCanonico(tipo) {
  return ALIAS_DISPOSITIVO[tipo] || tipo;
}

export function esVascular(tipo) {
  return VASCULARES.has(tipoCanonico(tipo));
}

// Accesos vasculares con lúmenes seleccionables (multilumen): mismo
// vocabulario que el editor de dispositivos (DispositivosEditor.jsx),
// exportado para que el lote de "Verificar en lote" (estación 4) sepa qué
// dispositivos jamás entran — un multilumen se verifica lumen por lumen, no
// de un vistazo colectivo. El catéter de hemodiálisis NO está aquí: es
// siempre de 2 lúmenes pero su verificación es "en uso / sellado con HNF",
// un chip propio, no esto.
const MULTILUMEN = new Set(['Catéter venoso central', 'PICC', 'Swan-Ganz']);
export function esMultilumen(tipo) {
  return MULTILUMEN.has(tipoCanonico(tipo));
}

// ── Puente de vocabulario recepción (F1) → evolución ──────────────────────────
// Los dos catálogos de dispositivos NO calzan 1:1: al recibir se eligen ids
// propios ('cvp', 'foley', 'sng'…) y la evolución habla en strings ('VVP',
// 'Catéter urinario permanente', 'SNG'…). Este mapa es la única traducción
// entre ambos; vivía en registroTurno.js y se MOVIÓ acá (2026-08-15) porque
// evolucionSistema.js es el módulo SIN dependencias del otro lado
// (registroTurno.js ya importa de aquí, así que importar de vuelta crearía un
// ciclo). Ahora el dominio de la evolución puede normalizar un dispositivo de
// recepción sin pedirle nada a nadie, y registroTurno.js lo importa.
// Historial: el mapa apuntaba al nombre viejo del Foley y el puente no
// calzaba nunca (lo cazó el test del sembrado, 30-jul).
//
// COMPLETADO a los 16 tipos (dictado del fundador en turno real, 2026-08-20:
// "pasar dispositivos invasivos completos arriba, porque están distintos en
// recepción y en evolución"): al recibir faltaban DVE, SOG, Swan-Ganz, PiCCO,
// Nexiva, catéter de hemodiálisis y marcapasos — o sea el paciente llegaba con
// un dispositivo que arriba no se podía ni nombrar, y abajo sí. Este mapa es
// ahora la ÚNICA lista de qué dispositivos existen al recibir: el catálogo de
// F1 (`CATALOGO_DISPOSITIVOS`) se DERIVA de aquí, y un test exige paridad con
// DISPOSITIVOS_TIPO en los dos sentidos. Los ids viejos no se tocan nunca: son
// lo que está guardado en los registros.
export const DISPOSITIVO_RECEPCION_A_EVOLUCION = {
  dve: 'DVE',
  sng: 'SNG',
  sog: 'SOG',
  tet: 'TOT',
  tqt: 'Traqueostomía',
  cvc: 'Catéter venoso central',
  swan: 'Swan-Ganz',
  picco: 'PiCCO',
  picc: 'PICC',
  la: 'Línea arterial',
  cvp: 'VVP',
  nexiva: 'Nexiva',
  chd: 'Catéter de hemodiálisis',
  marcapasos: 'Marcapasos',
  drenaje: 'Drenaje',
  foley: 'Catéter urinario permanente',
  // 2026-09-21: los tres del encargo del fundador. Ids nuevos y cortos, del
  // mismo estilo que los de arriba; los ids son lo que queda escrito en los
  // registros, así que no se tocan nunca más.
  sny: 'Sonda nasoyeyunal',
  gtt: 'Gastrostomía',
  smf: 'Flexiseal',
};

// Tipo de dispositivo en vocabulario de EVOLUCIÓN, venga de donde venga: id de
// recepción (F1), alias legado o el string canónico. Un solo lugar para las
// tres formas — cualquier lectura que mezcle ambos mundos pasa por aquí.
export function tipoDispositivoEvolucion(tipo) {
  return tipoCanonico(DISPOSITIVO_RECEPCION_A_EVOLUCION[tipo] || tipo);
}

// Estado de permeabilidad / reflujo del dispositivo.
export const DISPOSITIVO_ESTADO = [
  'Permeable', 'Refluye e infunde', 'Infunde sin resistencia', 'Refluye, no infunde',
  'No refluye', 'No evaluado',
];

// Signos de flebitis / infección (accesos vasculares), EN ORDEN DE SEVERIDAD:
// los chips se leen como una escala de izquierda a derecha, igual que las
// gravedades y las categorías de LPP.
//
// "Eritema en el sitio" y "Secreción en el sitio" se sumaron el 30-jul (aprobado
// por el fundador): venían del catálogo SITIO_INSERCION de la visita de
// enfermería, que en esto era MÁS fino que la evolución. La evolución colapsaba
// los cuatro estadios en "signos de flebitis", y son cosas distintas: el eritema
// aislado es el estadio precoz que todavía se revierte retirando la vía, y la
// secreción es lo que obliga a cultivar. Perder esa distinción al pasar de la
// visita a la evolución era perder información clínica en el camino.
//
// Aditivo y retrocompatible: los tres valores previos siguen siendo válidos y
// se imprimen igual (la prosa usa la etiqueta en minúscula, no un índice).
export const DISPOSITIVO_FLEBITIS = [
  'Sin signos de flebitis ni infección',
  'Eritema en el sitio',
  'Signos de flebitis',
  'Secreción en el sitio',
  'Signos de infección',
];

// Urgencia de cada estadio, para el color del chip (ver la nota de Chip.jsx: el
// color dice qué tan rápido hay que actuar, la etiqueta dice qué se encontró).
// Agrupado por CONDUCTA, que es lo que la escala tiene que comunicar de un
// vistazo, no por nombre del hallazgo:
//   estable  — nada que hacer, la vía sigue.
//   atencion — vigila y evalúa retirar la vía (eritema y flebitis).
//   critico  — cultiva y retira ahora (secreción y signos de infección).
// Dos estadios pueden compartir color sin perder información: la etiqueta ya
// los distingue y la prosa los escribe completos.
export const FLEBITIS_TONO = {
  'Sin signos de flebitis ni infección': 'estable',
  'Eritema en el sitio': 'atencion',
  'Signos de flebitis': 'atencion',
  'Secreción en el sitio': 'critico',
  'Signos de infección': 'critico',
};

// Lúmenes de un acceso multilumen (CVC / PICC / catéter de hemodiálisis).
export const DISPOSITIVO_LUMENES = ['1 lumen', '2 lúmenes', '3 lúmenes', '4 lúmenes', '5 lúmenes'];

// Nombres de los lúmenes por cantidad, en el orden en que se describen
// (dictado del fundador: "lumen distal: refluye, no refluye... y así con
// todos"). El funcionamiento se registra UNO POR UNO.
export const LUMEN_NOMBRES = {
  '2 lúmenes': ['distal', 'proximal'],
  '3 lúmenes': ['distal', 'medial', 'proximal'],
  '4 lúmenes': ['distal', 'medial 1', 'medial 2', 'proximal'],
  '5 lúmenes': ['distal', 'medial 1', 'medial 2', 'medial 3', 'proximal'],
};
// Funcionamiento de cada lumen. "En uso con drogas" documenta el NO evaluado
// legítimo: un lumen corriendo una BIC de sedación o DVA no se prueba, y eso
// se deja escrito (dictado del fundador).
export const LUMEN_ESTADOS = [
  'Refluye e infunde', 'Infunde, no refluye', 'Infunde con resistencia',
  'No refluye ni infunde', 'En uso con drogas (no se prueba)',
];

// Aspecto del débito de un drenaje.
export const DISPOSITIVO_ASPECTO = ['Seroso', 'Serohemático', 'Hemático', 'Purulento', 'Bilioso'];

// Ubicación sugerida por tipo de dispositivo: chips que rellenan el input de
// ubicación (libre siempre, por si la posición real no calza con la sugerencia).
// La clave es el tipo CANÓNICO: 'CVC' se renombró a 'Catéter venoso central' el
// 27-jul y este mapa se había quedado con la clave vieja, así que el CVC
// canónico (y toda tarjeta sembrada desde recepción) se quedaba sin chips. Un
// registro escrito con el nombre viejo sigue viéndolos: el lookup normaliza con
// `ubicacionesSugeridas` (jamás se migran datos).
//
// AMPLIADO el 2026-09-10 (pedido del fundador: "dispositivos invasivos, permitir
// colocar lugar y más detalle, excepto funcionalidad... SNG puede ser fosa nasal
// derecha e izquierda"): el sitio anatómico se sabe AL RECIBIR, viene en la
// entrega, y hasta hoy media docena de tipos no lo podía ni nombrar. SNG, TOT,
// DVE y marcapasos entran con su sitio real. El catéter urinario permanente, la
// SOG y la traqueostomía NO: su sitio es único, no hay nada que elegir, e
// inventar un chip donde no significa nada es ruido.
export const UBICACIONES_SUGERIDAS = {
  DVE: ['frontal derecho', 'frontal izquierdo'],
  SNG: ['fosa nasal derecha', 'fosa nasal izquierda'],
  // Entra por la nariz igual que la SNG, así que elige lado. La gastrostomía y
  // el Flexiseal NO: tienen sitio único, no hay nada que elegir
  // (mismo criterio con que el CUP y la traqueostomía quedaron fuera).
  'Sonda nasoyeyunal': ['fosa nasal derecha', 'fosa nasal izquierda'],
  TOT: ['comisura derecha', 'comisura izquierda'],
  Marcapasos: ['yugular derecha', 'subclavio derecho', 'subclavio izquierdo', 'femoral derecho', 'femoral izquierdo', 'epicárdico'],
  VVP: ['BD', 'BI', 'AD', 'AI', 'FND', 'FNI'],
  Nexiva: ['BD', 'BI', 'AD', 'AI', 'FND', 'FNI'],
  'Catéter venoso central': ['yugular derecha', 'yugular izquierda', 'subclavio derecho', 'subclavio izquierdo', 'femoral derecho', 'femoral izquierdo'],
  'Catéter de hemodiálisis': ['yugular derecha', 'yugular izquierda', 'subclavio derecho', 'subclavio izquierdo', 'femoral derecho', 'femoral izquierdo'],
  'Swan-Ganz': ['yugular derecha', 'yugular izquierda', 'subclavio derecho', 'subclavio izquierdo'],
  PiCCO: ['femoral derecha', 'femoral izquierda', 'axilar derecha', 'axilar izquierda'],
  PICC: ['BD', 'BI'],
  'Línea arterial': ['radial derecha', 'radial izquierda', 'femoral derecha', 'femoral izquierda'],
  Drenaje: ['pleural derecho', 'pleural izquierdo', 'abdominal', 'mediastínico'],
};

// Lookup ÚNICO de las ubicaciones sugeridas, normalizando el tipo (alias
// legados incluidos). Lo usan el editor de la evolución y la recepción de F1
// (vía `ubicacionesRecepcion` en registroTurno.js): un solo catálogo, un solo
// vocabulario en las dos superficies.
export function ubicacionesSugeridas(tipo) {
  return UBICACIONES_SUGERIDAS[tipoCanonico(tipo)] || [];
}

// Calibre sugerido por tipo (2026-09-10, mismo pedido que los sitios de arriba:
// "permitir colocar lugar y más detalle, excepto funcionalidad"). Los valores
// van SIN "#": la prosa lo antepone al imprimir (`lineaDispositivo`), y así el
// mismo string sirve para el chip y para el campo libre del editor.
//
// El TOT NO tiene calibre acá A PROPÓSITO: su medida la escribe la acción de
// intubación (`_medidasIot` / `completarMedidasIot` en registroTurno.js), que es
// la fuente única de los tres números del tubo. Un segundo escritor pisaría esa
// regla y el número quedaría dependiendo de quién tocó último.
export const CALIBRES_SUGERIDOS = {
  SNG: ['12', '14', '16', '18'],
  PICC: ['4', '5'],
  'Línea arterial': ['20', '22'],
  VVP: ['14', '16', '18', '20', '22', '24'],
  Nexiva: ['18', '20', '22', '24'],
  'Catéter urinario permanente': ['12', '14', '16', '18'],
};

export function calibresSugeridos(tipo) {
  return CALIBRES_SUGERIDOS[tipoCanonico(tipo)] || [];
}

let _dispSeq = 0;
export function nuevoDispositivo(tipo = '') {
  _dispSeq += 1;
  return {
    id: `disp-${_dispSeq}-${Math.random().toString(36).slice(2, 7)}`,
    tipo,
    calibre: '',     // sin "#": se antepone al imprimir
    ubicacion: '',   // libre: BD/BI, RD, AD, FND… (el código varía por dispositivo)
    dia: '',         // día de instalación (número)
    estado: null,    // permeabilidad/reflujo (vasculares)
    pushStop: false, // permeabilizado con técnica push-stop + SF (vasculares)
    flebitis: null,  // signos de flebitis/infección (vasculares)
    lumenes: null,   // CVC/PICC/catéter de hemodiálisis: cuántos lúmenes
    lumenDetalle: {},// { [nombreLumen]: estado } — funcionamiento uno por uno
    lumenManejo: '', // manejo del lumen problemático (permeabilización, aviso)
    hdTipo: null,    // catéter de hemodiálisis: 'Transitorio' | 'Tunelizado'
    hdUso: null,     // catéter de hemodiálisis: 'En uso' | 'Sellado con HNF'
    debito: '',      // drenajes: volumen del débito (ml)
    aspecto: null,   // drenajes: aspecto del débito
    ultimaCuracion: '', // fecha de la última curación del sitio (ISO 'YYYY-MM-DD')
    detalle: '',     // texto libre (observaciones, distribución de lúmenes…)
  };
}

// Formatea una fecha ISO 'YYYY-MM-DD' a 'DD/MM' (como la escribe una
// enfermera). '' si no es una fecha válida — jamás imprime basura.
export function fechaCuracionTexto(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
  return m ? `${m[3]}/${m[2]}` : '';
}

// ¿El dispositivo tiene algún dato cargado? (al menos el tipo o un campo).
export function dispositivoConContenido(d) {
  if (!d) return false;
  const conLumenDetalle = d.lumenDetalle && typeof d.lumenDetalle === 'object'
    && Object.values(d.lumenDetalle).some(Boolean);
  return Boolean(
    (d.tipo && d.tipo.trim()) || (d.calibre && String(d.calibre).trim()) ||
    (d.ubicacion && d.ubicacion.trim()) || (d.dia && String(d.dia).trim()) ||
    d.estado || d.pushStop || d.flebitis || d.lumenes ||
    conLumenDetalle || (d.lumenManejo && d.lumenManejo.trim()) || d.hdTipo || d.hdUso ||
    (d.debito && String(d.debito).trim()) || d.aspecto ||
    (d.ultimaCuracion && String(d.ultimaCuracion).trim()) ||
    (d.detalle && d.detalle.trim()),
  );
}

// ¿El dispositivo fue VERIFICADO, no solo anotado? Distingue los campos de
// verificación (permeabilidad, flebitis, lúmenes probados, débito, curación,
// observación) de los de identidad (tipo, calibre, ubicación, día, cuántos
// lúmenes tiene). Existe por el puente de recepción (30-jul): una entrada
// sembrada desde F1 trae tipo y día, y eso NO puede auto-marcar el ítem
// "invasivos corroborados" de la visita — verificar ES el dato.
export function dispositivoVerificado(d) {
  if (!d) return false;
  const conLumenDetalle = d.lumenDetalle && typeof d.lumenDetalle === 'object'
    && Object.values(d.lumenDetalle).some(Boolean);
  return Boolean(
    d.estado || d.pushStop || d.flebitis || conLumenDetalle ||
    (d.lumenManejo && d.lumenManejo.trim()) || d.hdUso ||
    (d.debito && String(d.debito).trim()) || d.aspecto ||
    (d.ultimaCuracion && String(d.ultimaCuracion).trim()) ||
    (d.detalle && d.detalle.trim()),
  );
}

// ── "Verificar en lote" (estación 4 de la visita, ola "El estándar Bupa") ───
// El lote NUNCA firma 'Permeable' (mismo porqué que el comentario de
// `dispositivoVerificado`: la permeabilidad es resultado de una PRUEBA —
// push-stop, reflujo—, no de una mirada; "verificar" ES el dato). Lo único
// inspeccionable de un vistazo colectivo es el SITIO de inserción: el lote
// firma "Sin signos de flebitis ni infección" (la misma opción estable del
// catálogo, DISPOSITIVO_FLEBITIS[0]) en los vasculares que aún no la
// declararon. Multilumen y catéter de hemodiálisis JAMÁS entran: el primero
// se verifica lumen por lumen (`LUMEN_ESTADOS`, chip por chip) y el segundo
// con su propio "en uso / sellado con HNF" — ninguno de los dos es "un
// vistazo". La permeabilidad y el estado por lumen siguen SIEMPRE chip por
// dispositivo, sin excepción.
export function dispositivosPendientesVerificar(dispositivos) {
  const lista = Array.isArray(dispositivos) ? dispositivos : [];
  return lista.filter((d) => (
    esVascular(d?.tipo) && !esMultilumen(d?.tipo)
    && tipoCanonico(d?.tipo) !== 'Catéter de hemodiálisis'
    && dispositivoConContenido(d)
    && !DISPOSITIVO_FLEBITIS.includes(d?.flebitis)
  ));
}

// Aplica el lote: SOLO el campo inspeccionable (flebitis "sin signos"), en
// los dispositivos elegidos (por id, para que "Este no" pueda excluir uno).
// Pura; devuelve la MISMA lista si no hay nada que verificar. UN patch para
// todo el lote (no N setState, mismo contrato que "El resto, sin novedad").
export function verificarDispositivosEnLote(dispositivos, ids) {
  const lista = Array.isArray(dispositivos) ? dispositivos : [];
  const set = new Set(Array.isArray(ids) ? ids.filter(Boolean) : []);
  if (!set.size) return dispositivos;
  let cambio = false;
  const next = lista.map((d) => {
    if (!set.has(d?.id)) return d;
    cambio = true;
    return { ...d, flebitis: 'Sin signos de flebitis ni infección' };
  });
  return cambio ? next : dispositivos;
}

// Línea de evolución de un dispositivo (o null si está vacío).
export function lineaDispositivo(d) {
  if (!dispositivoConContenido(d)) return null;
  let head = (d.tipo || 'Dispositivo').trim();
  // Catéter de hemodiálisis: transitorio/tunelizado va pegado al tipo, y es
  // SIEMPRE de 2 lúmenes (dictado del fundador) — se imprime aunque no se
  // haya marcado.
  if (d.tipo === 'Catéter de hemodiálisis') {
    if (d.hdTipo) head += ` ${String(d.hdTipo).toLowerCase()}`;
    if (!DISPOSITIVO_LUMENES.includes(d.lumenes)) head += ' 2 lúmenes';
  }
  // Lúmenes va pegado al tipo, antes del calibre (ej: "CVC 3 lúmenes #7…").
  if (DISPOSITIVO_LUMENES.includes(d.lumenes)) head += ` ${d.lumenes}`;
  if (d.calibre && String(d.calibre).trim()) head += ` #${String(d.calibre).trim()}`;
  if (d.ubicacion && d.ubicacion.trim()) head += ` ${d.ubicacion.trim()}`;
  let linea = head;
  if (d.dia !== undefined && d.dia !== null && String(d.dia).trim() !== '') {
    linea += `, día ${String(d.dia).trim()}`;
  }
  const cola = [];
  // Catéter de hemodiálisis: en uso o sellado con HNF (el sello es EL dato de
  // continuidad: el próximo turno sabe que hay heparina que aspirar).
  if (d.tipo === 'Catéter de hemodiálisis' && d.hdUso) {
    cola.push(d.hdUso === 'En uso' ? 'en uso' : 'sellado con HNF');
  }
  if (DISPOSITIVO_ESTADO.includes(d.estado)) cola.push(d.estado.toLowerCase());
  // Funcionamiento POR lumen, en el orden distal → proximal del catálogo
  // (dictado del fundador: "describir funcionamiento uno por uno").
  const nombres = LUMEN_NOMBRES[d.lumenes] || [];
  const porLumen = nombres
    .filter((n) => d.lumenDetalle && LUMEN_ESTADOS.includes(d.lumenDetalle[n]))
    .map((n) => `lumen ${n} ${d.lumenDetalle[n].charAt(0).toLowerCase()}${d.lumenDetalle[n].slice(1)}`);
  if (porLumen.length) cola.push(porLumen.join(', '));
  if (d.lumenManejo && d.lumenManejo.trim()) cola.push(d.lumenManejo.trim());
  if (d.pushStop) cola.push('técnica push-stop con SF');
  if (DISPOSITIVO_FLEBITIS.includes(d.flebitis)) cola.push(d.flebitis.toLowerCase());
  // Drenajes: débito + aspecto van juntos en una sola frase ("débito 150 ml
  // serohemático"); si solo hay aspecto (sin volumen aún), va solo.
  if (d.debito && String(d.debito).trim() !== '') {
    const asp = DISPOSITIVO_ASPECTO.includes(d.aspecto) ? ` ${d.aspecto.toLowerCase()}` : '';
    cola.push(`débito ${String(d.debito).trim()} ml${asp}`);
  } else if (DISPOSITIVO_ASPECTO.includes(d.aspecto)) {
    cola.push(`aspecto ${d.aspecto.toLowerCase()}`);
  }
  // Fecha de la última curación del sitio de inserción (dictado 2026-07-18):
  // dato de continuidad para el próximo turno. Solo si es fecha válida.
  const curacion = fechaCuracionTexto(d.ultimaCuracion);
  if (curacion) cola.push(`última curación ${curacion}`);
  if (d.detalle && d.detalle.trim()) cola.push(d.detalle.trim());
  if (cola.length) linea += `; ${cola.join(', ')}`;
  return `${linea}.`;
}

// Líneas de todos los dispositivos cargados (o []), EN ORDEN CÉFALO-CAUDAL
// (dictado del fundador: "sng, tot, cvc yd, la rd, cup"): se ordena por el
// índice del tipo en DISPOSITIVOS_TIPO; tipos fuera de catálogo van al final
// conservando el orden de ingreso (sort estable).
export function compilarDispositivos(dispositivos) {
  if (!Array.isArray(dispositivos)) return [];
  const orden = (d) => {
    const i = DISPOSITIVOS_TIPO.indexOf(tipoCanonico(d?.tipo));
    return i === -1 ? DISPOSITIVOS_TIPO.length : i;
  };
  return dispositivos.slice().sort((a, b) => orden(a) - orden(b)).map(lineaDispositivo).filter(Boolean);
}

// Brazo / extremidad para los brazaletes.
export const BRAZOS = [
  { id: 'ESD', label: 'ESD', full: 'extremidad superior derecha' },
  { id: 'ESI', label: 'ESI', full: 'extremidad superior izquierda' },
  { id: 'EID', label: 'EID', full: 'extremidad inferior derecha' },
  { id: 'EII', label: 'EII', full: 'extremidad inferior izquierda' },
];
function brazoFull(id) {
  return BRAZOS.find((b) => b.id === id)?.full || null;
}

export const COPY = {
  titulo: 'Evolución por sistema',
  bajada:
    'Registra tu turno por aparato, con notas de evolución donde lo necesites. Arma la evolución de enfermería ordenada por sistema.',
  cta: 'Generar evolución',
  vacio: 'Marca un hallazgo, un signo o escribe una nota en al menos un sistema.',
};

export function estadoInicial() {
  return {
    turno: null,
    sexo: null, // 'F' | 'M' | null — concordancia de género del texto
    // Apertura: estado general (bueno/regular/malo) y condición (estable →
    // extremadamente grave). Claves opcionales: drafts viejos no las traen y
    // el texto se comporta idéntico a antes (regla fijada por test).
    estadoGeneral: null,
    condicion: null,
    // { [sistemaId]: { hallazgos: string[], nota: string } }
    sistemas: {},
    // Antecedentes del paciente (contexto clínico, no PII): da coherencia a la
    // evolución y se le pasa a la IA en el pulido. NO entra al pipeline de
    // diagnóstico del PAE (eso sesgaría el dx); aquí es solo redacción.
    // `anamnesis`: texto libre PEGADO (historia breve del paciente) — a
    // diferencia de dx/mórbidos/quirúrgicos, es el punto de mayor riesgo de
    // PII del módulo; se banner-ea en la UI (AntecedentesCard) y solo se pasa
    // como contexto a la IA cuando NO tiene PII (ver construirContextoPaciente).
    // Nunca se imprime en buildEvolucion — es trasfondo, no contenido copiado.
    antecedentes: { dx: '', morbidos: '', quirurgicos: '', anamnesis: '' },
    brazaleteBrazo: null,
    // Verificación del brazalete al recibir (chequeo de seguridad de la ficha):
    // 'presente' | 'ausente' | 'ilegible' | null (no verificado). Verificar ES
    // el dato — un brazalete ausente registrado a tiempo es seguridad del
    // paciente, no una falta. Clave opcional: drafts viejos no la traen y el
    // texto se comporta idéntico a antes.
    brazaleteEstado: null,
    alergias: { tiene: false, brazo: null, detalle: '' },
    // Recuerdo del examen físico por sistema: cuáles sistemas han registrado
    // la comparación con el examen de ingreso ('sin-cambios' / 'hay-cambios').
    // Clave opcional: drafts viejos no la traen (retrocompatible).
    examenRecuerdos: {},
  };
}

// Antecedentes con contenido (alguno de los campos no vacío).
function antecedentesState(state) {
  const a = state?.antecedentes || {};
  return {
    dx: typeof a.dx === 'string' ? a.dx.trim() : '',
    morbidos: typeof a.morbidos === 'string' ? a.morbidos.trim() : '',
    quirurgicos: typeof a.quirurgicos === 'string' ? a.quirurgicos.trim() : '',
    anamnesis: typeof a.anamnesis === 'string' ? a.anamnesis.trim() : '',
  };
}
export function tieneAntecedentes(state) {
  const a = antecedentesState(state);
  return Boolean(a.dx || a.morbidos || a.quirurgicos || a.anamnesis);
}

// Contexto del paciente en una línea, para pasárselo a la IA en el pulido
// (Dx + antecedentes + alergias + anamnesis). Devuelve '' si no hay nada.
export function construirContextoPaciente(state) {
  const a = antecedentesState(state);
  const partes = [];
  if (a.dx) partes.push(`Dx: ${a.dx}`);
  if (a.morbidos) partes.push(`Antecedentes mórbidos: ${a.morbidos}`);
  if (a.quirurgicos) partes.push(`Antecedentes quirúrgicos: ${a.quirurgicos}`);
  const al = state?.alergias;
  if (al?.tiene || al?.detalle?.trim()) {
    partes.push(`Alergias: ${al.detalle?.trim() || 'conocidas'}`);
  }
  // Anamnesis: trasfondo para que la IA redacte con más coherencia — NUNCA
  // contenido que se copie a la evolución (el prompt de pulir ya lo exige).
  // Es texto libre pegado por el usuario: el punto de mayor riesgo de PII de
  // todo el módulo. Por eso solo se pasa si NO se detectó ningún dato
  // identificable; si el usuario no la corrige, el contexto simplemente la
  // omite (nunca la sanea a medias).
  if (a.anamnesis && !detectPii(a.anamnesis).hit) {
    partes.push(`Anamnesis: ${a.anamnesis}`);
  }
  return partes.join('. ');
}

// ── Guarda PII sobre las notas libres ─────────────────────────────────────────
// Sistemas cuya nota de evolución trae un dato identificable del paciente de
// ALTA precisión (nombre, RUT, teléfono…). La nota es el único texto 100%
// libre del documento y un nombre TIPEADO ahí (no pegado) se colaba sin aviso
// — incidente real del piloto Bupa (2026-07-24). Gatea el pulido IA. Usa
// detectPiiDuro (no el detector amplio): fechas o "cama 4" son texto clínico
// legítimo y no deben bloquear. `valores` son los fragmentos detectados, para
// que el aviso diga QUÉ corregir.
export function sistemasConNotaPii(state) {
  return SISTEMAS
    .map((sis) => {
      const { hit, matches } = detectPiiDuro(sistemaState(state, sis.id).nota);
      return hit ? { id: sis.id, label: sis.label, valores: matches.map((m) => m.valor) } : null;
    })
    .filter(Boolean);
}

// Concordancia de género: si el paciente es femenino, ajusta a la forma -a los
// adjetivos clínicos de género que emite la evolución (paciente como sujeto).
// Solo cambia palabras conocidas — no toca el resto del texto.
const FEMENINO = {
  Conectado: 'Conectada', Orientado: 'Orientada', Desorientado: 'Desorientada', Confuso: 'Confusa',
  Somnoliento: 'Somnolienta', Agitado: 'Agitada', Inquieto: 'Inquieta', Combativo: 'Combativa',
  Sedado: 'Sedada', Tranquilo: 'Tranquila', Cooperador: 'Cooperadora', Despierto: 'Despierta',
  Adaptado: 'Adaptada', Acoplado: 'Acoplada', Perfundido: 'Perfundida', Normocéfalo: 'Normocéfala', Supinado: 'Supinada',
  Hipotenso: 'Hipotensa', Normotenso: 'Normotensa', Hipertenso: 'Hipertensa',
  Bradicárdico: 'Bradicárdica', Normocárdico: 'Normocárdica', Taquicárdico: 'Taquicárdica',
  Eupneico: 'Eupneica', Taquipneico: 'Taquipneica', Bradipneico: 'Bradipneica',
};
function femeninoDe(palabra) {
  if (FEMENINO[palabra]) return FEMENINO[palabra];
  const cap = palabra.charAt(0).toUpperCase() + palabra.slice(1);
  const fem = FEMENINO[cap];
  return fem ? fem.charAt(0).toLowerCase() + fem.slice(1) : palabra;
}
export function generizarFemenino(texto) {
  return String(texto || '').replace(/[A-Za-zÁÉÍÓÚáéíóúÑñ]+/g, femeninoDe);
}

// ── Insumos del pulido IA (ola "El estándar Bupa", PR C) ─────────────────────
// El pulido v2 suelta la redacción del COLLAGE de catálogo (frases que MiPAE
// pegó una tras otra) pero mantiene intocable lo que ESCRIBIÓ el autor. Estas
// dos funciones puras son las que le dicen al prompt y a las redes qué es qué:
// `fragmentosAutor` marca el texto del autor, `clavesVerificacion` marca el
// contenido de catálogo que no puede desaparecer del bloque donde vive.

// Normalización compartida por los helpers de abajo (minúsculas, sin tildes):
// las raíces se comparan contra el pulido, que puede capitalizar distinto.
function normalizarClave(t) {
  return String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Texto DEL AUTOR dentro de la evolución: la nota libre por sistema, los campos
// de TEXTO LIBRE del catálogo, el detalle libre de cada dispositivo y el
// detalle de alergias. Es exactamente el mismo texto que ya viaja dentro del
// borrador (cero camino nuevo de PII) y encima se filtra con la MISMA guarda
// dura que buildEvolucion aplica a la nota: un nombre o RUT tipeado no viaja,
// ni siquiera marcado como texto del autor.
//
// Los campos de texto libre se derivan de CAMPOS_POR_SISTEMA (`tipo: 'text'`
// = lo TIPEÓ la enfermera, aunque buildEvolucion después lo imprima dentro de
// una frase de MiPAE) y se filtran con `camposVisibles`, la MISMA función que
// decide qué imprime el documento: así un valor viejo que quedó en un campo
// hoy oculto por su `dependeDe` no viaja a la IA sin estar en el borrador.
// Auditoría del PR C (2026-08-16): el prompt v2 declaraba "todo lo demás es
// catálogo y es tuyo para reescribir", y con eso "Corrección administrada:
// gluconato de calcio, insulina + SG, bicarbonato" se pulía a "en corrección
// según indicación médica", con las tres drogas fuera del registro clínico.
// Devuelve [{ origen, texto }] en el orden del documento.
export function fragmentosAutor(state) {
  const s = state || estadoInicial();
  const out = [];
  const agregar = (origen, valor) => {
    const t = typeof valor === 'string' ? valor.trim() : '';
    if (!t || detectPiiDuro(t).hit) return;
    out.push({ origen, texto: t });
  };
  for (const sis of SISTEMAS) {
    const nombre = sis.label.split(' · ')[0];
    const st = sistemaState(s, sis.id);
    agregar(nombre, st.nota);
    for (const def of camposVisibles(sis.id, st.campos)) {
      if (def?.tipo !== 'text') continue;
      agregar(`${nombre}, ${def.label}`, st.campos?.[def.k]);
    }
    for (const d of st.dispositivos) {
      if (dispositivoConContenido(d)) agregar(`${nombre}, ${(d.tipo || 'dispositivo').trim()}`, d.detalle);
    }
  }
  agregar('Alergias', s.alergias?.detalle);
  return out;
}

// Campos de catálogo cuyo valor marcado DEBE sobrevivir al pulido, por sistema.
// No es un catálogo paralelo: son ids reales de CAMPOS_POR_SISTEMA (drogas,
// aislamiento, ATB y TRR — lo que el fundador nombró como "esto no se puede
// perder"). Los hallazgos rápidos de cada sistema entran solos, sin tabla.
const CAMPOS_CLAVE_VERIFICACION = {
  neuro: ['sedoanalgesia', 'osmoterapia'],
  hemodinamia: ['dva'],
  renal: ['trr'],
  infecto: ['aislamiento', 'atbEsquema'],
};

// Abreviaturas que el pulido puede usar legítimamente en vez del nombre
// completo (el NORTE de nivel escribe "NAD", no "noradrenalina"). Sin esta
// tabla la red daría falso positivo justo en el estilo que queremos. Clave =
// raíz derivada del catálogo; valor = formas alternativas aceptadas.
const ALIAS_CLAVE = {
  noradre: ['nad', 'norepinefrina'],
  adrenal: ['adr', 'epinefrina'],
  nitrogl: ['ntg'],
  nitropr: ['nps'],
  midazol: ['mdz'],
  dexmede: ['dex', 'precedex'],
  hiperto: ['sht'],
  hemodia: ['hdi'],
  continu: ['trrc'],
};

// Raíz de una palabra clave: la palabra más larga del valor, truncada a 7
// caracteres para tolerar plural y flexión ("noradrenalina" → "noradre",
// "Aislamiento de contacto" → "aislami"). null si no queda nada verificable.
function raizDeValor(valor) {
  const palabras = normalizarClave(valor).split(/[^a-z0-9]+/).filter(Boolean);
  if (!palabras.length) return null;
  const ordenadas = palabras.slice().sort((a, b) => b.length - a.length);
  const larga = ordenadas.find((p) => p.length >= 5);
  if (larga) return larga.slice(0, 7);
  const corta = ordenadas[0];
  return corta && corta.length >= 3 ? corta : null;
}

// Claves de catálogo que el pulido debe conservar, con el bloque donde viven.
// Devuelve [{ bloque, label, raices }] — `raices` es una lista de formas
// aceptadas (la derivada + sus abreviaturas), basta que UNA aparezca.
//
// Deliberadamente NO se verifican los valores negativos ("Sin aislamiento",
// "Sin terapia osmótica"): son ausencias, y exigir su palabra literal era la
// principal fuente de falsos positivos en la calibración con los escenarios
// E1/E2/E3. La pérdida de una afirmación es lo que importa.
// `superados` (opcional, 2026-08-18): las claves cuya narración quedó superada
// por la trayectoria (camposSuperadosPorTrayectoria) NO se exigen al pulido —
// el borrador ya no las narra, y exigirlas descartaría TODOS los pulidos de
// ese documento (la red pide lo que el documento dice, no lo que los datos
// guardan).
export function clavesVerificacion(state, { superados = null } = {}) {
  const s = state || estadoInicial();
  const vistas = new Set();
  const out = [];
  for (const sis of SISTEMAS) {
    const bloque = sis.label.split(' · ')[0];
    const st = sistemaState(s, sis.id);
    const supCampos = superados?.[sis.id]?.campos || [];
    const valores = [
      ...sis.opciones.filter((o) => st.hallazgos.includes(o)),
      ...(CAMPOS_CLAVE_VERIFICACION[sis.id] || []).filter((k) => !supCampos.includes(k)).flatMap((k) => {
        const v = st.campos[k];
        if (Array.isArray(v)) return v;
        return typeof v === 'string' ? [v] : [];
      }),
    ];
    for (const valor of valores) {
      const texto = String(valor || '').trim();
      if (!texto || /^sin\s/i.test(texto) || detectPiiDuro(texto).hit) continue;
      const raiz = raizDeValor(texto);
      if (!raiz) continue;
      const clave = `${bloque}|${raiz}`;
      if (vistas.has(clave)) continue;
      vistas.add(clave);
      out.push({ bloque, label: texto, raices: [raiz, ...(ALIAS_CLAVE[raiz] || [])] });
    }
  }
  return out;
}

// Cuántas líneas de dispositivo tiene que traer el bloque "Invasivos" del
// documento, contadas DESDE EL ESTADO con la misma función que las imprime
// (compilarDispositivos). Es el piso que la red del pulido vigila: fundir dos
// dispositivos en una línea es perder uno.
// Con `trayectoria` y `superadosTrayectoria` (los mismos que recibe
// buildEvolucion) descuenta las tarjetas que el documento calla: si el piso se
// contara desde el estado entero, la red exigiría una línea que el borrador ya
// no trae y descartaría TODOS los pulidos de ese documento.
export function lineasInvasivosEsperadas(state, { trayectoria = null, superadosTrayectoria = null } = {}) {
  const s = state || estadoInicial();
  const omitidas = new Set(tarjetasInvasivosOmitidas(trayectoria, superadosTrayectoria));
  const lista = sistemaState(s, 'invasivos').dispositivos;
  return compilarDispositivos(omitidas.size ? lista.filter((d) => !omitidas.has(d?.id)) : lista).length;
}

// Ids de tarjeta de Invasivos que el documento NO imprime: las que la película
// declaró superadas (`superados.invasivos.dispositivos`, cada una con el
// sistema `portador` cuya línea cuenta la salida) y SOLO si ese portador trae
// líneas en esta impresión. Sin portador la tarjeta vuelve a imprimirse:
// omisión con portador vivo, la misma regla que los campos. Fuente única para
// buildEvolucion y para el piso del pulido: contar uno sin el otro es el bug.
export function tarjetasInvasivosOmitidas(trayectoria, superadosTrayectoria) {
  const lista = superadosTrayectoria?.invasivos?.dispositivos;
  if (!Array.isArray(lista) || !lista.length) return [];
  return lista
    .filter((t) => t && t.id && t.portador && Array.isArray(trayectoria?.[t.portador]) && trayectoria[t.portador].length)
    .map((t) => t.id);
}

// ── Persistencia (borrador local por usuario) ─────────────────────────────────
function claveBorrador(userId) {
  return `mipae_evolucion_draft_${userId || 'anon'}`;
}
export function cargarEvolucion(userId) {
  try {
    const raw = localStorage.getItem(claveBorrador(userId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? { ...estadoInicial(), ...data } : null;
  } catch {
    return null;
  }
}
export function guardarEvolucion(userId, state) {
  try { localStorage.setItem(claveBorrador(userId), JSON.stringify(state)); } catch { /* noop */ }
}
export function limpiarEvolucion(userId) {
  try {
    localStorage.removeItem(claveBorrador(userId));
    localStorage.removeItem(claveRefBorrador(userId));
  } catch { /* noop */ }
}

// Paciente-turno del borrador libre, para la evolución semanal de la cuenta
// gratuita (Términos §6, planGating.js). Vive JUNTO al borrador y no en la
// pantalla montada: reabrir el módulo con el mismo borrador sigue siendo la
// misma evolución, y "Empezar una evolución nueva" (limpiarEvolucion) estrena
// otra. Dura lo que el turno más largo (24 horas): un borrador retomado otro
// día es la evolución de otro turno, igual que un registro de MiPAE en turno,
// que se borra al terminar el suyo. Sin almacenamiento devuelve null y la
// pantalla usa una referencia propia.
const VIGENCIA_REF_BORRADOR_MS = 24 * 60 * 60 * 1000;
const REF_VALIDA = /^[A-Za-z0-9_-]{6,64}$/;
function claveRefBorrador(userId) {
  return `${claveBorrador(userId)}:ref`;
}
export function refBorradorEvolucion(userId, ahora = Date.now()) {
  try {
    const guardada = JSON.parse(localStorage.getItem(claveRefBorrador(userId)) || 'null');
    if (guardada && REF_VALIDA.test(guardada.ref || '') && typeof guardada.desde === 'number'
      && ahora - guardada.desde >= 0 && ahora - guardada.desde < VIGENCIA_REF_BORRADOR_MS) {
      return guardada.ref;
    }
    // Mismo formato que nuevoId de registroTurno (no se importa: ese módulo
    // depende de este).
    const ref = `${ahora.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(claveRefBorrador(userId), JSON.stringify({ ref, desde: ahora }));
    return ref;
  } catch {
    return null;
  }
}

// Estado de un sistema, tolerante a claves ausentes.
//
// Nota: la `tendencia` se retiró del modelo (decisión de producto: "Estable",
// "Mejora"… sin contexto no es evolución clínica, el detalle estructurado la
// reemplaza). Un draft viejo de localStorage puede seguir trayendo esa clave
// en `s.tendencia` — se ignora a propósito, nunca se lee ni se propaga.
export function sistemaState(state, id) {
  const s = state?.sistemas?.[id] || {};
  return {
    hallazgos: Array.isArray(s.hallazgos) ? s.hallazgos : [],
    nota: typeof s.nota === 'string' ? s.nota : '',
    escalas: s.escalas && typeof s.escalas === 'object' ? s.escalas : {},
    campos: s.campos && typeof s.campos === 'object' ? s.campos : {},
    dispositivos: Array.isArray(s.dispositivos) ? s.dispositivos : [],
    // "Evaluado — sin hallazgos": el enfermero revisó el sistema y está normal.
    // Sin esto el semáforo no distingue "no evaluado" de "evaluado y normal"
    // (oro clínico); modelo listo ahora, el editor de la ronda (3/4) agrega el
    // chip. Si el usuario además marca hallazgos/campos, el detalle manda
    // (ver buildEvolucion) — el flag y el detalle son mutuamente excluyentes
    // en la UI que viene, pero acá el modelo tolera ambos sin romper.
    sinHallazgos: Boolean(s.sinHallazgos),
  };
}

// El sistema ENTERO para reescribirlo: el modelo público normalizado de
// `sistemaState` encima de las claves privadas que dejan los puentes
// (`_examenCampos`, `_examenLineasProyectadas`, `_concienciaExamen`,
// `_contextosEscalas`). `sistemaState` sirve para LEER; todo escritor que
// reconstruye un sistema parte de aquí. Partir de `sistemaState` borraba lo
// proyectado por el examen en cada toque de la Mesa (auditoría de la entrega
// de turno, 2026-10-20): con "Evaluado · sin hallazgos" en hemodinamia el
// documento cambiaba "Hemodinámicamente inestable" por "Normotenso,
// normocárdico…", y en neuro el sopor constatado al recibir por "Vigil,
// Glasgow 15…", hasta que los puentes volvían a correr. La `tendencia`
// retirada sigue sin propagarse (ver arriba).
export function sistemaCompleto(state, id) {
  const crudo = state?.sistemas?.[id];
  const { tendencia, ...resto } = crudo && typeof crudo === 'object' && !Array.isArray(crudo) ? crudo : {};
  return { ...resto, ...sistemaState(state, id) };
}

function sistemaConContenido(state, sis) {
  const { hallazgos, nota, escalas, campos, dispositivos, sinHallazgos } = sistemaState(state, sis.id);
  if (sis.id === 'neuro' && state?.sistemas?.neuro?._concienciaExamen?.some(x => x.valor && !detectPiiDuro(x.valor).hit)) return true;
  if (sinHallazgos) return true; // evaluado y normal también es contenido
  if (hallazgos.length > 0 || nota.trim().length > 0) return true;
  if (dispositivos.some(dispositivoConContenido)) return true;
  return sistemaTieneDetalle(sis.id, escalas, campos);
}

// ── Descripción basal por sistema ─────────────────────────────────────────────
// Lo que "Evaluado — sin hallazgos" DECLARA en la película del turno, escrito
// una vez y bien (misma decisión que NORMAL_SEGMENTO del examen, 2026-07-11:
// "Sin hallazgos en el turno." era débil como documento). Redactadas en clave
// de TURNO (lo evaluado y sin novedad durante el turno), sin afirmar soportes
// ni dispositivos que el flag no puede saber. Catálogo cerrado → PII-safe.
// Estilo dictado por el fundador (2026-07-16): la basal se lee como la
// evolución real de un paciente sin novedad, en dos tiempos (estado del turno
// y luego "Al examen físico, ..."), no como una frase de formulario. Neuro y
// hemodinamia son literalmente su dictado; el resto sigue el mismo molde con
// prudencia (la basal solo afirma lo que "evaluado y normal" puede firmar:
// nada de soportes, dispositivos ni exámenes que el flag no puede saber).
export const NORMAL_SISTEMA = {
  neuro: 'Vigil, Glasgow 15, orientado en tiempo y espacio, conectado con el medio, obedece órdenes simples. Sin dolor, en reposo relativo. Al examen físico normocéfalo, pupilas isocóricas con reflejo fotomotor presente.',
  hemodinamia: 'Normotenso, normocárdico en ritmo sinusal, afebril. Al examen físico bien perfundido, pulsos presentes, llene capilar menor a 3 segundos, sin edema ni mottling.',
  ventilacion: 'Vía aérea nativa sin requerir oxigenoterapia, no refiere disnea. Al examen físico, murmullo pulmonar presente sin ruidos agregados.',
  gastro: 'Alimentación por vía oral con buena ingesta y régimen bien tolerado; metabolismo estable. Deposiciones durante el día. Al examen físico, abdomen blando, depresible e indoloro, con ruidos hidroaéreos presentes.',
  renal: 'Diuresis espontánea (chata, pato o pañal), con débito conservado. Orina de características normales, sin trastornos hidroelectrolíticos.',
  // Corrección contra la propia regla del catálogo (2026-08-15): estas dos
  // basales afirmaban cosas que el flag NO puede saber. Infecto declaraba
  // "parámetros inflamatorios en rango" (un examen de laboratorio) y "sin
  // antibioterapia" (estado de una terapia); hemato declaraba "con
  // tromboprofilaxis farmacológica" (una terapia activa). Ninguna de las tres
  // se constata mirando al paciente, que es lo único que "evaluado y sin
  // hallazgos" firma. El dato real tiene su propio camino: la rama de hemato
  // y las verificaciones de profilaxis de la visita lo escriben cuando existe.
  infecto: 'Afebril durante el turno, sin signos de infección.',
  hemato: 'Sin signos de sangrado durante el turno.',
  tegumentario: 'Piel indemne, sin lesiones nuevas durante el turno. Zonas de apoyo sin eritema ni signos de presión.',
  invasivos: 'Accesos y dispositivos sin incidentes durante el turno. Sitios de inserción sin signos de flebitis ni infección.',
  procedimientos: 'Sin procedimientos adicionales durante el turno.',
};

// La basal de un sistema TAL COMO va a quedar escrita, ya generizada según el
// sexo del paciente. Existe para que preview y documento salgan de la MISMA
// función: los previews pintaban `NORMAL_SISTEMA[id]` crudo ("orientado") y el
// documento imprimía la versión generizada ("orientada"), así que el gesto de
// lote y el toggle "Registra: …" mostraban un texto distinto del que firmaban.
export function basalSistema(sistemaId, sexo) {
  const texto = NORMAL_SISTEMA[sistemaId] || 'Sin hallazgos en el turno.';
  return sexo === 'F' ? generizarFemenino(texto) : texto;
}

// ── Sembrado normal: lo que el puente trajo y no contradice la basal ──────────
// ¿El ÚNICO contenido de este sistema son valores que los puentes sembraron
// desde el chequeo general (F2) y que además dicen "todo normal"?
//
// El porqué (regresión encontrada en el conteo adversarial de la ola "El
// estándar Bupa", 2026-08-16): en el orden real de trabajo la visita va antes
// que la Mesa, así que el paciente estable llega a la Mesa con "Sin sedación",
// "Sin dolor", "Aire ambiente" y "Sin drogas vasoactivas" ya sembrados. Con
// eso el sistema "tiene contenido", el chip "Evaluado · sin hallazgos"
// desaparecía y la basal rica de NORMAL_SISTEMA quedaba inalcanzable: el
// documento imprimía "Sin sedación. Sin dolor." donde antes decía "Vigil,
// Glasgow 15, orientado en tiempo y espacio…". La ola existe para que el
// documento salga MEJOR con menos toques, así que este predicado devuelve el
// chip (y la basal) a ese caso, que es el más común.
//
// Dos condiciones, ambas necesarias:
//  1. PROCEDENCIA: cada campo con valor está anotado en `_sembradoNormal` (lo
//     escribió el puente, no la enfermera).
//  2. VIGENCIA: su valor de HOY sigue declarando normalidad en el catálogo. Si
//     la enfermera corrigió un campo sembrado a un valor positivo ("Con
//     sedación"), la clave sigue en la lista pero el valor ya no es normal y la
//     basal rica mentiría, así que manda el detalle.
// Cualquier otro dato (un hallazgo, una nota, un dispositivo, un campo o una
// escala escritos a mano) lo descarta: ahí el detalle manda como siempre.
//
// Las ESCALAS pasan por las MISMAS dos condiciones desde que existe el puente
// escalas del examen → escalas de la Mesa (ola "El estándar Bupa"): antes
// ninguna escala podía venir de un puente, así que una escala con valores era
// necesariamente de la enfermera y bastaba con verla para descartar. Hoy una
// Braden 20 sembrada desde la visita es exactamente lo que la basal ya afirma,
// y sacaba al tegumentario del chip y del lote justo en el paciente sin
// lesiones. Su clave se anota como `sistema:escala:<id>` (namespace propio para
// no chocar con la de un campo) y su normalidad la decide el `nivel` del score.
//
// Clave `_sembradoNormal` OPCIONAL: un estado viejo que no la trae devuelve
// false y todo se comporta igual que antes (datos viejos se LEEN con adapters,
// jamás se migran; fijado por test).
export function soloSembradoNormal(state, sistemaId) {
  const marcadas = Array.isArray(state?._sembradoNormal) ? state._sembradoNormal : [];
  if (!marcadas.length) return false;
  const prefijo = `${sistemaId}:`;
  const sembradas = new Set(
    marcadas
      .filter((c) => typeof c === 'string' && c.startsWith(prefijo))
      .map((c) => c.slice(prefijo.length)),
  );
  if (!sembradas.size) return false;

  const st = sistemaState(state, sistemaId);
  if (st.hallazgos.length > 0 || st.nota.trim().length > 0) return false;
  if (st.dispositivos.some(dispositivoConContenido)) return false;
  // Mismas funciones de contenido que `sistemaTieneDetalle` (cero catálogo
  // paralelo): escalas por un lado, campos por el otro, para poder exigirle a
  // los campos la procedencia que a las escalas no aplica.
  // TODAS las escalas con valor, no solo las que hoy se ofrecen: el documento
  // imprime una escala puntuada aunque ya no se amerite (un RASS de la visita
  // en un paciente que la Mesa ve vigil, 2026-09-29), así que tiene que contar
  // aquí igual que en sistemaTieneDetalle.
  const escalasConValor = escalasDe(sistemaId)
    .filter((e) => escalaConValores(e.id, st.escalas?.[e.id]));
  if (!escalasConValor.every((e) => sembradas.has(`escala:${e.id}`)
    && valorNormalDeEscala(e.id, st.escalas[e.id]))) return false;
  const conValor = camposVisibles(sistemaId, st.campos).filter((c) => campoConValor(c, st.campos?.[c.k]));
  // Sin NADA con valor (ni campo ni escala) no hay procedencia que reconocer:
  // el sistema está vacío y el camino normal de `conDatos` ya lo cubre.
  if (!conValor.length && !escalasConValor.length) return false;
  return conValor.every((c) => sembradas.has(c.k) && valorNormalDeCampo(sistemaId, c.k, st.campos[c.k]));
}

// ── Escribir un sistema respetando la política flag/detalle ──────────────────
// Extraído de EvolucionEditor (2026-09-19) para que la Mesa y la calculadora
// rápida escriban por EL MISMO camino: son dos superficies del mismo acto, y
// una segunda copia de esta política habría dejado "sin hallazgos" colgando en
// una y no en la otra. La Mesa sigue siendo la dueña del estado; esto es la
// regla, no el estado.

// ¿El sistema tiene algún dato MÁS ALLÁ del flag "sin hallazgos"? Espeja
// sistemaConContenido sin el propio flag.
export function tieneOtroContenidoSistema(sistemaId, est) {
  if ((est.hallazgos || []).length > 0 || String(est.nota || '').trim().length > 0) return true;
  if ((est.dispositivos || []).some(dispositivoConContenido)) return true;
  return sistemaTieneDetalle(sistemaId, est.escalas, est.campos);
}

// Aplica un patch al sistema. Si lo que queda trae contenido real, "sin
// hallazgos" se suelta solo (flag y detalle son mutuamente excluyentes), salvo
// que ese contenido sea exactamente lo que el puente sembró diciendo normal:
// eso es lo que la basal ya afirma, así que el chip sigue disponible.
// PURA: devuelve el estado nuevo, no muta ni escribe.
export function conSistemaEvolucion(state, sistemaId, patch) {
  const merged = { ...sistemaCompleto(state, sistemaId), ...patch };
  const estadoCon = { ...state, sistemas: { ...(state?.sistemas || {}), [sistemaId]: merged } };
  const anula = tieneOtroContenidoSistema(sistemaId, merged) && !soloSembradoNormal(estadoCon, sistemaId);
  const sinHallazgos = anula ? false : merged.sinHallazgos;
  return { ...state, sistemas: { ...(state?.sistemas || {}), [sistemaId]: { ...merged, sinHallazgos } } };
}

// Guarda los valores de una escala en el sistema donde esa escala vive.
// Los valores se FUNDEN con los que ya había (mismo gesto que la Mesa: tocar
// un ítem no borra los otros).
export function conEscalaEvolucion(state, sistemaId, escalaId, vals) {
  const previas = sistemaState(state, sistemaId).escalas || {};
  const escalas = { ...previas, [escalaId]: { ...(previas[escalaId] || {}), ...vals } };
  const out = conSistemaEvolucion(state, sistemaId, { escalas });
  // La exención de `soloSembradoNormal` existe para lo que el PUENTE sembró
  // diciendo normal: eso es lo que la basal ya afirma, así que el chip
  // sobrevive. Una escala que la autora llenó a mano NO es eso.
  //
  // El agujero, encontrado ejecutando (2026-09-19): `soloSembradoNormal` solo
  // revisa las escalas VISIBLES del sistema, y la hoja puede abrir una que no
  // lo es (FOUR con "Sin sedación", que la Mesa no ofrece). Esa escala pasaba
  // por debajo del guard, la basal quedaba firmada, y `buildEvolucion`
  // imprimía la basal EN VEZ del detalle: el FOUR 16/16 recién guardado
  // desaparecía del documento con toast de confirmación y todo. Un dato
  // clínico que la autora registró jamás se pierde en silencio.
  if (!escalaConValores(escalaId, escalas[escalaId])) return out;
  if (!sistemaState(out, sistemaId).sinHallazgos) return out;
  return { ...out, sistemas: { ...out.sistemas, [sistemaId]: { ...sistemaCompleto(out, sistemaId), sinHallazgos: false } } };
}

// ¿Guardar esta escala va a RETIRAR la basal que la autora firmó con el chip
// "Evaluado, sin hallazgos"? Se pregunta ANTES de escribir para poder
// decírselo: la app puede agregar lo que ella pide, pero no puede quitarle en
// silencio algo que ella declaró (auditoría 2026-09-19).
export function guardarEscalaRetiraBasal(state, sistemaId, escalaId, vals) {
  if (!sistemaState(state, sistemaId).sinHallazgos) return false;
  return sistemaState(conEscalaEvolucion(state, sistemaId, escalaId, vals), sistemaId).sinHallazgos === false;
}

// ── Lo sembrado que NO se narra (eco de una fuente más específica) ───────────
// Un puente puede responder un campo o una escala cuya información OTRA parte
// del mismo bloque ya escribió mejor. El caso que lo estrenó (ola "El estándar
// Bupa"): la lesión del mapa de piel escribe su frase completa en la nota del
// tegumentario ("LPP categoría II en sacro, de 3 x 2 cm, presente al recibir,
// cobertura con hidrocoloide") y el mismo puente responde `integridad`,
// `lppLocalizacion`, `lppDimensiones` y `lppCobertura` para que la Mesa no los
// vuelva a preguntar. Narrar ambos imprimía la misma lesión dos veces.
//
// OJO, y esto NO es un detalle de redacción: la omisión es solo de NARRACIÓN.
// Los campos se siembran igual, y ESO es lo que cierra
// `MINIMOS_SISTEMA.tegumentario = ['integridad']`. Si alguien "simplifica"
// después dejando de sembrarlos porque total no se imprimen, el tegumentario
// del paciente con LPP vuelve a quedar incompleto en la Mesa y la enfermera lo
// contesta de nuevo, que es justo lo que el puente existe para evitar.
//
// DOS condiciones, ambas necesarias, y el fail-safe apunta siempre a NARRAR:
//  1. PROCEDENCIA: el valor de hoy es idéntico al que el puente escribió
//     (libros `_puenteCampos` / `_puenteEscalas`). Si la enfermera lo CORRIGIÓ,
//     su corrección es información nueva y el documento no puede tragársela.
//  2. PORTADOR VIVO: el ancla (`_camposSinEco` / `_escalasSinEco`) sigue
//     presente en la nota EFECTIVA del bloque. Sin esta condición la omisión
//     era un kill-switch silencioso sobre un dato médico-legal (hallazgo R3 de
//     la auditoría del PR G): la nota es un textarea libre que la enfermera
//     reescribe o borra, y la guarda de PII dura la descarta ENTERA al
//     imprimir, así que la LPP podía quedarse sin ningún portador en el
//     documento y sin ninguna señal. Por eso la nota que se pasa acá tiene que
//     ser la que el documento va a imprimir de verdad, no el texto crudo.
//
// Exportada porque el preview y el documento tienen que salir de la MISMA
// decisión: toda superficie que compile un bloque con `compilarSistemaDetalle`
// le pasa este mismo par (el "Así queda" de la Mesa y la línea "En tu evolución
// (sistema): …" del chequeo general). Sin esto el preview promete una línea que
// el documento no va a tener, que es la divergencia que la regla prohíbe.
//
// Claves OPCIONALES: un estado que no las trae devuelve listas vacías y el
// documento sale byte a byte como antes (datos viejos se LEEN con adapters,
// jamás se migran; fijado por test).
export function narracionOmitida(state, sistemaId, { campos, escalas, nota } = {}) {
  const texto = typeof nota === 'string' ? nota : '';
  const prefijo = `${sistemaId}:`;
  const recoger = (anclas, libro, valores) => {
    if (!anclas || typeof anclas !== 'object') return [];
    const out = [];
    for (const [clave, ancla] of Object.entries(anclas)) {
      if (!clave.startsWith(prefijo)) continue;
      if (!ancla || !texto.includes(ancla)) continue;                          // portador vivo
      const k = clave.slice(prefijo.length);
      const sembrado = libro && typeof libro === 'object' ? libro[clave] : undefined;
      if (sembrado === undefined) continue;
      if (JSON.stringify(valores?.[k]) !== JSON.stringify(sembrado)) continue; // procedencia
      out.push(k);
    }
    return out;
  };
  return {
    omitir: recoger(state?._camposSinEco, state?._puenteCampos, campos),
    omitirEscalas: recoger(state?._escalasSinEco, state?._puenteEscalas, escalas),
  };
}

// ¿Hay algo registrado? (apertura, sistemas, brazalete o alergias).
export function tieneContenido(state) {
  const s = state || estadoInicial();
  if (aperturaEvolucion(s)) return true;
  if (SISTEMAS.some((sis) => sistemaConContenido(s, sis))) return true;
  if (tieneAntecedentes(s)) return true;
  if (s.brazaleteBrazo || s.brazaleteEstado) return true;
  if (s.alergias?.tiene || s.alergias?.detalle?.trim()) return true;
  return false;
}

// ── Completitud de la evolución (motor del rediseño: anillo, semáforo, coach) ──
// Cuántos sistemas tienen contenido vs el total. Base para el indicador de
// progreso, la grilla con semáforo y la sugerencia de próximo paso.
export function completitudEvolucion(state) {
  const s = state || estadoInicial();
  const porSistema = SISTEMAS.map((sis) => {
    const conDatos = sistemaConContenido(s, sis);
    return {
      id: sis.id,
      label: sis.label.split(' · ')[0],
      conDatos,
      // Completitud honesta: con datos Y con sus mínimos clínicos respondidos
      // (o la basal declarada). El check de la grilla habla este idioma.
      minimoCompleto: conDatos && minimosPendientes(s, sis.id).length === 0,
      // Para la barra del tile: preguntas raíz respondidas sobre las aplicables.
      minimos: minimosDeSistema(s, sis.id),
    };
  });
  const conDatos = porSistema.filter((x) => x.conDatos).length;
  const total = SISTEMAS.length;
  // Siempre sobre los diez sistemas, en todos los servicios (decisión del
  // fundador 2026-09-28 tras la auditoría del preview: el servicio del
  // paciente no esconde sistemas). Es el número que ven la Mesa, el riel, el
  // hub, Benito y la fila de la jefatura (`turnos_completitud`, CHECK 0..10).
  return { porSistema, conDatos, total, pct: total ? Math.round((conDatos / total) * 100) : 0 };
}

// ── Mínimos por sistema (misión "<10 minutos", tabla validada por el fundador
// 2026-07-23) ─────────────────────────────────────────────────────────────────
// El "mínimo clínico" de cada sistema NO es un catálogo nuevo: son las
// preguntas raíz que la evolución por sistema ya hace (norma de máxima
// coherencia; ids reales de CAMPOS_POR_SISTEMA, fijado por test). Un mínimo
// solo cuenta como pendiente si su dependeDe se cumple con los campos actuales
// (la conciencia no se pide bajo sedación); declarar la basal del sistema
// ("Evaluado, sin hallazgos") cubre el mínimo completo. invasivos y
// procedimientos no llevan lista: su contenido corre solo (dispositivos del
// registro y acciones del turno).
export const MINIMOS_SISTEMA = {
  neuro: ['sedacion', 'conciencia', 'dolor'],
  hemodinamia: ['presion', 'fc', 'ritmo', 'dvaSoporte'],
  ventilacion: ['viaAerea', 'spo2'],
  gastro: ['regimen'],
  renal: ['diuresisVia', 'orina'],
  infecto: ['estadoFebril', 'aislamiento'],
  hemato: ['tromboprofilaxis'],
  tegumentario: ['integridad'],
  invasivos: [],
  procedimientos: [],
};

const _campoVacio = (v) =>
  v === undefined || v === null ||
  (typeof v === 'string' && !v.trim()) ||
  (Array.isArray(v) && !v.length) ||
  (typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length);

// Mínimos aún sin responder de un sistema: [{ k, label }]. Vacío si la basal
// está declarada o el sistema no define lista.
export function minimosPendientes(state, sistemaId) {
  const ids = MINIMOS_SISTEMA[sistemaId] || [];
  if (!ids.length) return [];
  const st = sistemaState(state, sistemaId);
  if (st.sinHallazgos) return [];
  const defs = CAMPOS_POR_SISTEMA[sistemaId] || [];
  const out = [];
  for (const k of ids) {
    const def = defs.find((c) => c.k === k);
    if (!def) continue;
    if (!cumpleDependencia(def.dependeDe, st.campos)) continue;
    if (_campoVacio(st.campos[k])) out.push({ k, label: def.label });
  }
  return out;
}

// Cuántas preguntas raíz APLICABLES tiene el sistema y cuántas están
// respondidas (idea 3 del brainstorm 2026-09-28: la barra del tile). Las
// ramas cerradas no cuentan (misma regla que minimosPendientes); con la
// basal declarada el mínimo está completo; sin lista, { 0, 0 }.
export function minimosDeSistema(state, sistemaId) {
  const ids = MINIMOS_SISTEMA[sistemaId] || [];
  if (!ids.length) return { hechos: 0, total: 0 };
  const st = sistemaState(state, sistemaId);
  const defs = CAMPOS_POR_SISTEMA[sistemaId] || [];
  const aplicables = ids.filter((k) => {
    const def = defs.find((c) => c.k === k);
    return def && cumpleDependencia(def.dependeDe, st.campos);
  });
  const total = aplicables.length;
  if (st.sinHallazgos) return { hechos: total, total };
  const hechos = aplicables.filter((k) => !_campoVacio(st.campos[k])).length;
  return { hechos, total };
}

// ¿El sistema alcanzó su mínimo clínico? Requiere contenido; con lista de
// mínimos, además todos los aplicables respondidos (o la basal declarada).
export function sistemaMinimoCompleto(state, sistemaId) {
  const sis = SISTEMAS.find((x) => x.id === sistemaId);
  if (!sis || !sistemaConContenido(state, sis)) return false;
  return minimosPendientes(state, sistemaId).length === 0;
}

// ── "El resto, sin novedad" (Mesa expedita, ola "El estándar Bupa") ──────────
// Mismo patrón que `firmarRestoSinHallazgos` del examen (examenFisico.js): el
// flujo real es "traigo 2-3 sistemas con hallazgos en la cabeza, el resto se
// firma de un toque". Candidatos = sistemas SIN datos (`conDatos` falso) y NO
// contradichos por lo que el chequeo general (F2) o la recepción (F1) ya
// constataron. El invariante no se toca: la UI muestra las frases basales
// ANTES de firmar — el usuario firma lo que constató, no un checkbox ciego.
//
// `ctx` (opcional): { examen, dispositivos } — `examen` es el examen físico del
// INGRESO (la foto: soportes del chequeo general, conciencia y estado
// hemodinámico constatados al recibir), `dispositivos` es `registro.dispositivos`
// (F1, recepción). Sin `ctx`, ninguna exclusión clínica aplica (solo `conDatos`
// filtra): el módulo standalone permite editar un borrador libre, sin registro
// elegido, y ahí no hay examen ni dispositivos que leer — con registro elegido
// sí los pasa, así que el gesto trae las mismas exclusiones que en la ficha.
//
// Por qué se lee examen/registro DIRECTO en vez de confiar solo en `conDatos`:
// los puentes vivos (PR A) ya bajan sedación/vía aérea/DVA a la evolución en la
// mayoría de los casos (y ahí `conDatos` ya los excluye solo), pero el lote
// puede tocarse ANTES de que el puente corra (Mesa abierta sin pasar por las
// estaciones de la visita) — esta lectura es defensa adicional, no un catálogo
// paralelo (evolucionSistema.js no puede importar de examenFisico.js: crearía
// un ciclo, examenFisico.js ya importa de acá).
//
// Estado de conciencia comprometido y hemodinamia inestable son EL caso donde
// esta red es la ÚNICA que existe: ninguno de los dos siembra la evolución por
// diseño (precedente `ex.conciencia`, la foto no es la película), así que sin
// esta exclusión el lote firmaba "Vigil, Glasgow 15, orientado" sobre un
// paciente que se recibió en sopor. La lista de valores comprometidos se
// escribe literal porque el catálogo CONCIENCIA vive en examenFisico.js y no
// se puede importar (ciclo); un test la fija CONTRA ese catálogo, así que si
// CONCIENCIA cambia, truena.
const CONCIENCIA_COMPROMETIDA = new Set(['Somnoliento', 'Sopor', 'Agitación psicomotora', 'No responde']);

function contradiccionesLote(ctx) {
  const excluidos = new Set();
  const ex = ctx?.examen && typeof ctx.examen === 'object' ? ctx.examen : {};
  const s = ex.soportes && typeof ex.soportes === 'object' ? ex.soportes : {};
  const dispositivos = Array.isArray(ctx?.dispositivos) ? ctx.dispositivos : [];
  // Los dispositivos pueden venir en CUALQUIERA de los dos vocabularios: el
  // call-site de la ficha pasa `registro.dispositivos` de F1 (ids: 'sng',
  // 'foley', 'tet'…) y un ctx armado desde la evolución pasa los strings
  // ('SNG', 'Catéter urinario permanente'…). Comparar solo contra los strings
  // dejaba estas exclusiones muertas en el camino real (auditoría 2026-08-15).
  const tieneTipo = (tipos) => dispositivos.some((d) => tipos.includes(tipoDispositivoEvolucion(d?.tipo)));
  // Sedación declarada en los soportes del chequeo general: la basal de neuro
  // afirma "Vigil, Glasgow 15" — contradicción directa.
  if (s.sedacion === 'Con sedación') excluidos.add('neuro');
  // Conciencia comprometida al recibir: misma contradicción con la basal de
  // neuro, por el otro camino (un paciente puede estar soporoso sin sedación).
  if (CONCIENCIA_COMPROMETIDA.has(ex.conciencia)) excluidos.add('neuro');
  // SNG (id 'sng' en F1: "SNG / SNY") u SOG: la basal de gastro afirma
  // alimentación por vía oral.
  if (tieneTipo(['SNG', 'SOG'])) excluidos.add('gastro');
  // Catéter urinario permanente (id 'foley' en F1): la basal de renal afirma
  // diuresis espontánea.
  if (tieneTipo(['Catéter urinario permanente'])) excluidos.add('renal');
  // Vía aérea artificial o un soporte de O₂ real: la basal de ventilación
  // afirma vía aérea nativa SIN requerir oxigenoterapia. "Aire ambiente" es
  // justamente esa basal (no la contradice), así que no excluye.
  if (s.respiratorio && s.respiratorio !== 'Aire ambiente') excluidos.add('ventilacion');
  // TOT o traqueostomía declarados como dispositivo (ids 'tet' / 'tqt' en F1):
  // la vía aérea artificial contradice la basal aunque el chequeo general no
  // se haya llenado — el dispositivo ES la constatación.
  if (tieneTipo(['TOT', 'Traqueostomía'])) excluidos.add('ventilacion');
  // DVA declaradas: la basal de hemodinamia afirma normotenso sin soporte.
  if (s.dvaSoporte === 'Con drogas vasoactivas') excluidos.add('hemodinamia');
  // Inestabilidad hemodinámica constatada al recibir: la basal afirma
  // "Normotenso, normocárdico en ritmo sinusal". Igual que la conciencia, este
  // dato no cruza a la evolución por diseño, así que esta es su única red.
  if (s.hemoEstado === 'Inestable') excluidos.add('hemodinamia');
  // Marcapasos: la basal afirma ritmo sinusal. Solo existe en el catálogo de
  // la evolución (DISPOSITIVOS_TIPO); F1 no ofrece el tipo, y por eso este
  // caso llega únicamente por el vocabulario de evolución.
  if (tieneTipo(['Marcapasos'])) excluidos.add('hemodinamia');
  return excluidos;
}

// Sistemas candidatos al lote: sin datos PROPIOS y sin contradicción clínica.
// Se devuelve label además de id porque la UI despliega la frase basal de cada
// uno ANTES de firmar (misma transparencia que el examen).
//
// "Sin datos propios" incluye al sistema que solo trae sembrado-normal (ola "El
// estándar Bupa", 2026-08-16): en el orden real de trabajo la visita corre
// antes que la Mesa, así que neuro, ventilación y hemodinamia llegan con los
// valores normales del chequeo general y `conDatos` los sacaba del lote justo
// en el caso más común, el paciente estable. Lo que el puente sembró diciendo
// "todo normal" es exactamente lo que la basal ya afirma, así que no es un dato
// que compita con ella.
//
// Las exclusiones clínicas mandan igual: un sistema contradicho (sedación, TOT,
// SNG, catéter urinario, DVA, conciencia comprometida, hemodinamia inestable,
// marcapasos) NUNCA entra al lote, tenga o no contenido sembrado. Y un valor
// sembrado que NO es normal ("Con sedación") tampoco vuelve candidato al
// sistema: `soloSembradoNormal` lo descarta por su cuenta.
export function sistemasSinNovedadCandidatos(state, ctx = {}) {
  const { porSistema } = completitudEvolucion(state);
  const excluidos = contradiccionesLote(ctx);
  return porSistema
    .filter((s) => (!s.conDatos || soloSembradoNormal(state, s.id)) && !excluidos.has(s.id))
    .map(({ id, label }) => ({ id, label }));
}

// Declara "sin hallazgos" en un conjunto de sistemas (por id). Pura; devuelve
// el MISMO objeto si la lista viene vacía. La firma sigue siendo consciente:
// quien llama muestra las frases basales ANTES (invariante intocado) y permite
// excluir sistemas puntuales del lote ("Este no").
export function firmarSistemasSinHallazgos(state, ids) {
  const lista = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (!lista.length) return state;
  const s = state || estadoInicial();
  const sistemas = { ...(s.sistemas || {}) };
  for (const id of lista) {
    sistemas[id] = { ...sistemaCompleto(s, id), sinHallazgos: true };
  }
  return { ...s, sistemas };
}

// Declara "sin hallazgos" en todos los candidatos del lote. Pura; devuelve el
// MISMO objeto si no queda nada por firmar.
// Es el CONTRATO del gesto a nivel de dominio y lo ejercitan los tests de
// invariantes (composición con los puentes, guarda de basal firmada); la Mesa
// usa el par granular (candidatos + firmarSistemasSinHallazgos) porque el
// "Este no" filtra ids antes de firmar. No es un export muerto: es la firma
// de referencia del gesto completo.
export function firmarSistemasSinNovedad(state, ctx = {}) {
  const candidatos = sistemasSinNovedadCandidatos(state, ctx);
  if (!candidatos.length) return state;
  return firmarSistemasSinHallazgos(state, candidatos.map((c) => c.id));
}

// Próxima mejor acción (el "coach"): qué sistemas siguen sin registro y un
// mensaje accionable. DESCRIPTIVO, no evaluación (auditoría editorial
// 2026-09-28, E05): un sistema sin texto no es una falta ni una deuda, así
// que el mensaje dice "sin registro" y nombra cuáles; "te faltan N" y
// "listo para entregar" salieron.
export function proximoPasoEvolucion(state) {
  const { porSistema, conDatos } = completitudEvolucion(state);
  const faltan = porSistema.filter((x) => !x.conDatos).map((x) => x.label);
  if (conDatos === 0) {
    return { tipo: 'empezar', faltan, mensaje: 'Sin registros todavía. Habla tu turno o toca un sistema.' };
  }
  if (faltan.length === 0) {
    return { tipo: 'listo', faltan: [], mensaje: 'Los sistemas tienen registro. Revisa la evolución antes de entregar.' };
  }
  const muestra = faltan.slice(0, 2).join(' y ');
  const mensaje = faltan.length <= 2
    ? `Sistemas sin registro: ${muestra}.`
    : `Sistemas sin registro: ${muestra} y ${faltan.length - 2} más.`;
  return { tipo: 'continuar', faltan, mensaje };
}

// Mayúscula / minúscula inicial respetando el resto (para prosa). No toca
// acrónimos/abreviaturas (CUP, SNG, CVC…): si la primera palabra trae una
// mayúscula interna, se deja tal cual.
const esAbreviatura = (t) => /[A-ZÁÉÍÓÚÑ]/.test((t || '').split(' ')[0].slice(1));
const lowerFirst = (t) => (!t || esAbreviatura(t) ? t : t.charAt(0).toLowerCase() + t.slice(1));
const upperFirst = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// Texto libre estructurado (antecedentes): quita el punto final que el usuario
// haya tecleado — la línea que lo envuelve ya pone el suyo, y sin esto salían
// puntos dobles ("…reagudizado.."). Nunca se aplica a la nota libre (línea
// propia, con la puntuación del autor).
const sinPuntoFinal = (v) => String(v || '').trim().replace(/\.+$/, '');

// Encabezado del turno con la preposición correcta ("Turno de tarde", "Turno
// de noche"…) — antes era un `toLowerCase()` plano que producía "Turno noche"
// (sin "de"), gramaticalmente incorrecto.
const TURNO_ENCABEZADO = {
  Diurno: 'Turno diurno', Tarde: 'Turno de tarde', Noche: 'Turno de noche', Largo: 'Turno largo',
  '24 horas': 'Turno de 24 horas',
};

// Construye la evolución como una nota de enfermería: encabezado simple y un
// bloque por sistema (nombre en su línea, hallazgos, prosa agrupada por tema
// —campos y escalas juntos— y la narrativa libre). Devuelve
// { lineas: string[], texto }.
//
// Ya no emite una tendencia suelta ("Estable.", "Mejora; náuseas o vómitos.");
// se retiró del modelo porque una palabra sin contexto no aporta valor clínico
// — el detalle estructurado (campos/escalas) la reemplaza con información real.
// `trayectoria` (opcional, E4): las líneas de "se recibió / se decidió / se
// constató" por sistema, YA compuestas por lineasTrayectoriaPorSistema. Se
// inyectan al FRENTE del bloque de su sistema y se generizan (prosa de MiPAE).
// Sin la opción, el output es byte a byte el de siempre (fijado por test).
// `superadosTrayectoria` (opcional, 2026-08-18): { [sis]: {campos, escalas} }
// de camposSuperadosPorTrayectoria — las claves cuya narración como PRESENTE
// quedó superada por lo que la trayectoria constató (el "Sedado, con propofol"
// de la mañana conviviendo con "despierto, sin sedación" del turno real de
// Bupa). Doctrina "omisión con portador vivo": los DATOS no se tocan y la
// omisión solo aplica si la línea de trayectoria de ese sistema está EN este
// documento (sin portador, la narración vuelve). Sin la opción, byte a byte
// como siempre.
// ── Libro de la selección MANUAL de hallazgos (`_hallazgosManuales`) ────────
// Un hallazgo puede estar respaldado por la autora (lo marcó en la Mesa) y/o por
// uno o varios repartos de IA (libro `_prosa`). Son procedencias distintas y se
// guardan aparte: una corrección de una nota fuente retira el respaldo de la IA,
// jamás la selección manual vigente. Esta es LA ruta del producto para marcar o
// desmarcar a mano (EvolucionEditor): escribe el hallazgo en el sistema y lo
// anota (o lo saca) del libro. Desmarcar a mano saca el hallazgo de la lista,
// que es lo que la autora decidió, y también su respaldo manual.
export function marcarHallazgoManual(state, sisId, hallazgo, on) {
  const s = state || estadoInicial();
  const sis = s.sistemas?.[sisId] || { hallazgos: [], nota: '' };
  const lista = Array.isArray(sis.hallazgos) ? sis.hallazgos : [];
  const hallazgos = on ? (lista.includes(hallazgo) ? lista : [...lista, hallazgo]) : lista.filter((h) => h !== hallazgo);
  const libro = { ...(s._hallazgosManuales || {}) };
  const propios = new Set(libro[sisId] || []);
  if (on) propios.add(hallazgo); else propios.delete(hallazgo);
  if (propios.size) libro[sisId] = [...propios]; else delete libro[sisId];
  const next = { ...s, sistemas: { ...(s.sistemas || {}), [sisId]: { ...sis, hallazgos } } };
  if (Object.keys(libro).length) next._hallazgosManuales = libro; else delete next._hallazgosManuales;
  return next;
}
export function esHallazgoManual(state, sisId, hallazgo) {
  return Boolean(state?._hallazgosManuales?.[sisId]?.includes(hallazgo));
}

// "Al examen físico" abre PÁRRAFO PROPIO en todos los sistemas (dictado del
// fundador 2026-09-07): punto aparte y una línea en blanco antes. Si aparece a
// media línea (". Al examen físico, …", como lo escribe el pulido a veces) se
// parte ahí. Varias líneas de examen seguidas forman UN solo párrafo. Puro e
// idempotente; sin "Al examen físico" el texto vuelve idéntico. Lo usan el
// documento (buildEvolucion) y el pulido (pulirEvolucion), así que la copia y
// el compartir heredan la separación.
// Reconoce las dos variantes ("Al examen físico" y "al examen físico al
// recibir", esta última la escribe la película) y normaliza la inicial en
// mayúscula al abrir el párrafo; la atribución "al recibir" se conserva.
const EXAMEN_RE = /\bal examen (?:f[ií]sico|de control)\b/i;
export function separarExamenFisico(texto) {
  const src = String(texto || '');
  if (!EXAMEN_RE.test(src)) return src;
  // Partir la línea donde el examen empieza a media frase. Sin lookbehind (los
  // WebView viejos no lo soportan): se cierra la frase anterior con punto, se
  // inserta el salto y se vuelve a partir por línea.
  const lineas = src.replace(/[.;:,]\s+(al examen (?:f[ií]sico|de control)\b)/gi, '.\n$1').split('\n')
    .map((l) => l.replace(/^(\s*)al examen f([ií])sico\b/, '$1Al examen f$2sico'));
  const out = [];
  const esExamen = (l) => /^Al examen (?:f[ií]sico|de control)\b/i.test(String(l).trim());
  for (let i = 0; i < lineas.length; i += 1) {
    const l = lineas[i];
    const anteriorExamen = i > 0 && esExamen(lineas[i - 1]);
    // Línea en blanco al abrir el párrafo del examen, y al cerrarlo si sigue
    // otra cosa: el párrafo es independiente por los dos lados.
    if (esExamen(l) && !anteriorExamen && out.length && out[out.length - 1].trim() !== '') out.push('');
    if (!esExamen(l) && anteriorExamen && l.trim() !== '' && out.length && out[out.length - 1].trim() !== '') out.push('');
    out.push(l);
  }
  return examenAlCierre(out.join('\n'), [...SISTEMAS.map(s => s.label.split(' · ')[0]), 'Antecedentes', 'Identificación y seguridad', 'Seguridad']);
}

export function buildEvolucion(state, { fecha, turno: turnoDelRegistro = null, incluirEncabezado = true, trayectoria = null, superadosTrayectoria = null } = {}) {
  const s = state || estadoInicial();
  // Concordancia de género: se aplica SOLO a los fragmentos que MiPAE genera
  // (hallazgos, prosa de campos, escalas integradas a la oración) — nunca a
  // la nota libre, los antecedentes, el detalle de dispositivos ni el detalle
  // de alergias, que son texto del propio usuario y no deben "corregirse".
  const generiza = (t) => (s.sexo === 'F' ? generizarFemenino(t) : t);

  // Ecos entre sistemas (dictado del fundador 2026-07-31): lo registrado en un
  // sistema que otro necesita decir (SHT con objetivo de natremia → renal).
  // Deterministas y solo hechos; la tabla vive en escalasEvolucion.
  const ecos = ecosEntreSistemas(s.sistemas || {});

  const seguridad = Object.values(s._riesgosSeguridad || {}).filter(t => identificarNotaRiesgo(t)).map(t => `${t}.`);
  const escalasSeguridad = ['braden', 'downton'];
  for (const sis of SISTEMAS) {
    for (const id of escalasSeguridad) {
      const r = scoreEscala(id, s.sistemas?.[sis.id]?.escalas?.[id]);
      const notaFuente = s.sistemas?.[sis.id]?.nota || '';
      const omitidas = narracionOmitida(s, sis.id, { campos: s.sistemas?.[sis.id]?.campos || {}, escalas: s.sistemas?.[sis.id]?.escalas || {}, nota: detectPiiDuro(notaFuente).hit ? '' : notaFuente }).omitirEscalas;
      if (r && !omitidas.includes(id)) seguridad.push(`${r.texto.split(' — ')[0].replace(/\.$/, '')}.`);
    }
  }
  const recogerRiesgos = texto => String(texto || '').split('\n').map(l => {
    if (/^Al examen de control: (Braden|Downton)\b/.test(l.trim()) && !/Glasgow|RASS|EVA|CPOT|CAM/i.test(l)) { seguridad.push(l.trim()); return null; }
    const riesgo = identificarNotaRiesgo(l);
    if (!riesgo) {
      if (esLineaRiesgosParaSeguridad(l)) { seguridad.push(l.trim()); return null; }
      // El reparto puede entregar piel o procedimientos y riesgo como dos
      // oraciones en el mismo renglón. Solo sale la oración que reconoce el
      // mismo catálogo de prosa; la frase clínica vecina queda literal. No
      // separar cuando el párrafo aporta un tiempo o una relación causal.
      const contextoCompartido = /\b(?:luego|despu[eé]s|posteriormente|antes|inicialmente|al inicio|al ingreso|al recibir|en el control|en la valoraci[oó]n|por la ma[ñn]ana|por la tarde|por ello|por esto|por este motivo|esta evaluaci[oó]n)\b|\d{1,2}:\d{2}|\d{4}-\d{2}-\d{2}|\b(?:el|del|fecha)\s+\d{1,2}\/\d{1,2}/i;
      if (contextoCompartido.test(l)) return l;
      const partes = l.split(/(?<=\.)(\s+)/);
      if (partes.length === 1) return l;
      let extraidas = 0;
      const restantes = partes.map((parte, i) => {
        if (i % 2) return '';
        if (esLineaRiesgosParaSeguridad(parte)) {
          seguridad.push(parte.trim());
          extraidas += 1;
          return '';
        }
        return parte + (partes[i + 1] || '');
      }).join('');
      return extraidas ? restantes.trim() || null : l;
    }
    if (riesgo.fuente !== 'recibido' || !s._riesgosSeguridad?.[riesgo.id]) seguridad.push(l.trim());
    return null;
  }).filter(l => l !== null).join('\n');
  const bloques = SISTEMAS
    .map((sis) => {
      const lineasTray = trayectoria?.[sis.id] || null;
      const lineasEco = ecos[sis.id] || null;
      // La trayectoria basta para que el sistema exista en el documento: un
      // paciente cuyo único dato de neuro es la película de la sedación
      // igual la entrega. Un eco también: el renal de un paciente en
      // osmoterapia documenta la natremia objetivo aunque no tenga más datos.
      // Las tarjetas de Invasivos que la película calla (ver el paso 4) no
      // cuentan como contenido: sin ellas, un bloque "Invasivos." vacío no se
      // imprime, igual que si nunca hubieran existido en el documento.
      const sinTarjeta = sis.id === 'invasivos' ? new Set(tarjetasInvasivosOmitidas(trayectoria, superadosTrayectoria)) : null;
      const sVisible = sinTarjeta?.size
        ? { ...s, sistemas: { ...(s.sistemas || {}), invasivos: { ...(s.sistemas?.invasivos || {}), dispositivos: (s.sistemas?.invasivos?.dispositivos || []).filter((d) => !sinTarjeta.has(d?.id)) } } }
        : s;
      if (!sistemaConContenido(sVisible, sis) && !lineasTray && !lineasEco && !sVisible.sistemas?.[sis.id]?._examenCampos) return null;
      const { hallazgos, nota, escalas, campos, dispositivos, sinHallazgos } = sistemaState(sVisible, sis.id);
      const halValidos = sis.opciones.filter((o) => hallazgos.includes(o))
        // El campo de vía aérea ya tiene su propia frase (o su portador en
        // la trayectoria). El chip del mismo dispositivo no añade un hecho.
        .filter(o => !(sis.id === 'ventilacion' && campos.viaAerea === 'Vía aérea artificial' && (
          (o === 'Traqueostomía' && campos.viaArtificial === 'TQT')
          || (o === 'TOT' && campos.viaArtificial === 'TOT')
        )));
      const nombre = sis.label.split(' · ')[0];
      // Basal rica POR SOBRE el detalle sembrado (ola "El estándar Bupa",
      // 2026-08-16): con "sin hallazgos" FIRMADO y un sistema cuyo único
      // contenido son los valores normales que el puente bajó del chequeo
      // general, el documento imprime la basal de NORMAL_SISTEMA y NO el
      // detalle. Clínicamente no se pierde nada: la basal AFIRMA lo mismo que
      // esos valores normales y además lo que el chequeo no pregunta ("Vigil,
      // Glasgow 15, orientado… Sin dolor" contiene al "Sin sedación. Sin
      // dolor."), mientras que dos frases sueltas son peor documento. Con
      // CUALQUIER dato que no sea sembrado-normal (un hallazgo, una nota, una
      // escala, un campo escrito a mano, un valor sembrado que no es normal),
      // `soloSembradoNormal` devuelve false y manda el detalle como siempre
      // (convivencia flag + detalle, fijada por test).
      const soloSembrado = sinHallazgos && soloSembradoNormal(s, sis.id);

      // La película manda sobre la foto: las claves superadas por la
      // trayectoria se callan SOLO con su portador vivo (las líneas de
      // trayectoria de este sistema, recién impresas arriba). Datos
      // intactos; el aviso de conflictosTrayectoria pide reconciliar.
      const sup = (lineasTray && superadosTrayectoria?.[sis.id]) || null;
      let notaImpresa = nota.trim() && !detectPiiDuro(nota).hit ? recogerRiesgos(nota.trim()) : '';
      const examenProyectado = sVisible.sistemas?.[sis.id]?._examenCampos;
      if (examenProyectado && !detectPiiDuro(examenProyectado).hit) {
        const lineasProyectadas = new Set(sVisible.sistemas?.[sis.id]?._examenLineasProyectadas || []);
        notaImpresa = notaImpresa.split('\n').map(l => {
          const prefijo = [...lineasProyectadas].find(p => l.trim().startsWith(p));
          return prefijo ? l.trim().slice(prefijo.length).trim() : l;
        }).filter(Boolean).join('\n').trim();
      }

      // Una línea de la nota que la película ya RELACIONÓ como antecedente
      // (el examen del tubo que se retiró, ver peliculaEstado) no se repite
      // como presente: se calla por coincidencia EXACTA de la línea entera y
      // solo con el portador vivo. Datos intactos en la nota.
      if (notaImpresa && sup?.notaLineas?.length) {
        const fuera = new Set(sup.notaLineas.map((l) => String(l).trim()));
        notaImpresa = notaImpresa.split('\n').filter((l) => !fuera.has(l.trim())).join('\n').trim();
      }
      // Prosa de la IA intercalada en la película por procedencia (ver
      // peliculaDelTurno): se saca de la nota SOLO si sigue ahí verbatim (línea
      // entera) y se inserta en su lugar del arco. Datos intactos.
      let tray = lineasTray ? [...lineasTray] : null;
      if (tray && notaImpresa && sup?.intercalados?.length) {
        const pendientes = [...sup.intercalados].sort((a, b) => a.indice - b.indice);
        let corrimiento = 0;
        for (const { texto: frag, indice } of pendientes) {
          const lineasNota = notaImpresa.split('\n');
          const fragLineas = String(frag).split('\n').map((l) => l.trim());
          let ini = -1;
          for (let i = 0; i + fragLineas.length <= lineasNota.length && ini < 0; i += 1) {
            if (fragLineas.every((fl, j) => lineasNota[i + j].trim() === fl)) ini = i;
          }
          if (ini < 0) continue;
          lineasNota.splice(ini, fragLineas.length);
          notaImpresa = lineasNota.join('\n').trim();
          tray.splice(indice + corrimiento, 0, ...fragLineas);
          corrimiento += fragLineas.length;
        }
      }
      const lineas = [];
      const concienciaExamen = sis.id === 'neuro' && !campos.conciencia
        ? sVisible.sistemas?.neuro?._concienciaExamen || [] : [];
      // La conciencia se conserva con el vocabulario observado; un sopor
      // sin profundidad especificada no se transforma en una opción distinta.
      if (concienciaExamen.length) {
        const valores = concienciaExamen.filter((x, i, xs) => x.valor && !detectPiiDuro(x.valor).hit
          && (i === 0 || x.valor !== xs[i - 1].valor));
        valores.forEach((x, i) => lineas.push(generiza(`${valores.length > 1
          ? `${x.contexto === 'de control' ? 'En el control' : 'Inicialmente'}, ${lowerFirst(x.valor)}`
          : lineasTray ? `En la valoración ${x.contexto === 'de control' ? 'de control' : 'inicial'}, ${lowerFirst(x.valor)}`
            : upperFirst(x.valor)}.`)));
      }
      // 0) La trayectoria del turno abre el bloque: es el arco del día, el
      // resto es el detalle. Prosa generada por MiPAE: se generiza.
      if (tray) for (const lt of tray) lineas.push(generiza(lt));
      // La nota efectiva conserva el estado que también marcó el catálogo.
      // Solo consolidar afirmaciones completas, sin procedencia temporal;
      // una nota rica o un control se mantienen para el editor clínico.
      const yaNarrado = catalogoYaNarradoEnNota(sVisible, sis.id, {
        nota: notaImpresa, campos, hallazgos: halValidos, generiza,
        trayectoria: lineasTray, superados: superadosTrayectoria?.[sis.id],
      });
      // 1) Hallazgos rápidos.
      const hallazgosNarrados = halValidos.filter(h => !yaNarrado(generiza(`${h}.`)));
      if (hallazgosNarrados.length) {
        const halJoin = hallazgosNarrados.map((h, i) => (i === 0 ? h : lowerFirst(h))).join(', ');
        lineas.push(generiza(`${halJoin}.`));
      }
      // 2) Prosa agrupada por tema: cada grupo clínico (conciencia, dolor,
      // soporte…) arma su propia oración con sus campos, con la escala de ESE
      // grupo integrada a la MISMA oración (ola "El estándar Bupa",
      // 2026-08-15: antes colgaba como viñeta aparte; antes de eso las
      // escalas quedaban todas juntas al final, lejos de lo que miden).
      // La nota EFECTIVA (la que de verdad va a imprimirse) se decide ANTES del
      // detalle, porque `narracionOmitida` la necesita: lo que el puente calla
      // solo se puede callar si el portador sigue vivo en el documento, y la
      // guarda de PII dura descarta la nota entera. Con PII, `notaImpresa` es
      // '' y la línea de campos vuelve a narrarse: nada se pierde en silencio.
      if (!soloSembrado) {
        const { omitir, omitirEscalas } = narracionOmitida(s, sis.id, { campos, escalas, nota: notaImpresa });
        const omisiones = [...omitir, ...(sup?.campos || [])];
        const omisionesEscalas = [...omitirEscalas, ...(sup?.escalas || []), ...escalasSeguridad];
        // Un campo aún idéntico a lo sembrado conserva procedencia de recepción.
        // El texto libre de un cambio no autoriza a inferir el soporte vigente.
        const hayRelato = notaImpresa.split('\n').some(l => l.trim() && !/^Al examen /i.test(l.trim()));
        const recibidos = Object.keys(campos).filter(k => hayRelato && !omisiones.includes(k)
          && Object.prototype.hasOwnProperty.call(s._puenteSoportes || {}, `${sis.id}:${k}`)
          && JSON.stringify(campos[k]) === JSON.stringify(s._puenteSoportes[`${sis.id}:${k}`]));
        if (recibidos.length) {
          const recepcion = compilarSistemaDetalle(sis.id, campos, {}, {
            omitir: [...omisiones, ...Object.keys(campos).filter(k => !recibidos.includes(k))],
          }).lineas;
          if (recepcion.length) lineas.push(generiza(`Se recibió ${recepcion.map(l => lowerFirst(l.replace(/\.$/, ''))).join('; ')}.`));
        }
        compilarSistemaDetalle(sis.id, campos, escalas, {
          omitir: [...omisiones, ...recibidos],
          omitirEscalas: omisionesEscalas,
        }).lineas.forEach((l) => {
          if (yaNarrado(generiza(l))) return;
          // Si la nota también describe esa escala, no presentar su medición
          // de la visita como si fuera simultánea. El contexto lo aporta la
          // foto fuente del puente, nunca se deduce del puntaje ni de la nota.
          const nombres = [...l.matchAll(/\b(Glasgow|FOUR|RASS|SAS|EVA|CPOT)\b/g)].map(m => m[1]);
          const id = nombres.length === 1 ? nombres[0].toLowerCase() : null;
          const contexto = id ? sVisible.sistemas?.[sis.id]?._contextosEscalas?.[id] : null;
          // El contexto no puede extenderse a campos manuales que comparten
          // oración con la escala (dolor, ubicación o sedación de otro momento).
          const soloEscala = id && compilarSistemaDetalle(sis.id, {}, { [id]: escalas[id] }).lineas.includes(l);
          const anclada = soloEscala && contexto && JSON.stringify(contexto.valores) === JSON.stringify(escalas[id])
            && new RegExp(`\\b${nombres[0]}\\b`, 'i').test(notaImpresa);
          lineas.push(generiza(anclada
            ? `${contexto.contexto === 'control' ? 'En el control' : 'En la valoración inicial'}, ${lowerFirst(l)}`
            : l));
        });
      }
      // 2b) Ecos de otros sistemas (prosa generada por MiPAE: se generiza).
      if (lineasEco) for (const le of lineasEco) lineas.push(generiza(le));
      const examenCampos = String(sVisible.sistemas?.[sis.id]?._examenCampos || '').split('\n')
        .filter(l => !(sup?.examenLineas || []).includes(l)).join('\n');
      if (examenCampos && !detectPiiDuro(examenCampos).hit) lineas.push(generiza(examenCampos));
      // 3) Narrativa libre del turno (el protagonista) — nunca se generiza:
      // es la voz del usuario, no un fragmento generado por MiPAE.
      // PERO jamás con PII dura: un nombre o RUT tipeado en la nota NO viaja
      // al documento (incidente 2026-08-11: "sebastian undurraga" en
      // minúscula avanzaba hasta el "Así queda"). La UI avisa qué corregir;
      // esta es la garantía de que el documento no lo imprime aunque el
      // aviso se ignore. El texto queda intacto en el campo para corregirlo.
      // El bloque de PIEL se lee de cabeza a pies cuando trae muchos hallazgos
      // (pedido del fundador desde su turno, 2026-08-29). Todos los puentes de
      // prosa escriben en esta misma nota y APPENDEAN, así que sin esto el
      // bloque queda en orden de llegada. Es una permutación de párrafos: no
      // cambia una palabra ni mueve lo que no supo clasificar (ver
      // lib/ordenCefaloCaudal.js). Solo tegumentario: es el único sistema que
      // se recorre por anatomía.
      if (notaImpresa) {
        lineas.push(sis.id === 'tegumentario' ? ordenarCefaloCaudal(notaImpresa) : notaImpresa);
      }
      // 4) Dispositivos invasivos, uno por línea (calibre, ubicación, día,
      // estado) — tampoco se generiza: mezcla detalle libre del dispositivo.
      // Menos las tarjetas cuya salida ya contó la película (extubación,
      // retiro): se callan con su portador vivo, que puede ser la línea de
      // OTRO sistema (el tubo se cuenta en ventilación). Datos intactos.
      // (Ya filtradas arriba, en `sVisible`.)
      compilarDispositivos(dispositivos).forEach((l) => lineas.push(l));

      // La basal firmada NO desaparece por tener trayectoria (revisor E4): si
      // las únicas líneas son la película y el flag "sin hallazgos" está
      // marcado, la normalidad que la enfermera constató y firmó se imprime
      // igual, después de la película. Sin esto, marcar "sin hallazgos" en
      // neuro y llenar la sedación borraba la basal del documento en silencio.
      // Con solo sembrado-normal la basal ocupa el lugar del detalle que se
      // acaba de omitir (puede quedar después de la película o de un eco: la
      // basal cierra el bloque, igual que en el caso de la trayectoria).
      if (sinHallazgos && (soloSembrado || (lineasTray && lineas.length === lineasTray.length))) {
        lineas.push(basalSistema(sis.id, s.sexo));
      }
      if (lineas.length) {
        let narrativa = lineas.join('\n');
        // Una trayectoria contiene relaciones temporales protegidas: allí
        // manda la película. Sin ella, ordenar oraciones completas por tema.
        if (sis.id === 'neuro' && !lineasTray && concienciaExamen.length) narrativa = ordenarNarrativaNeurologica(narrativa);
        if (sis.id === 'infecto') narrativa = compactarFiebreRepetida(narrativa);
        return `${nombre}.\n${separarExamenFisico(narrativa)}`;
      }
      // "Evaluado — sin hallazgos": si no hay NINGÚN detalle pero el usuario
      // marcó el flag, es oro clínico (evaluado y normal, no "no evaluado") y
      // se expande a la descripción basal del sistema — el documento describe
      // lo constatado. Si hubiera detalle, el detalle manda. La basal es prosa
      // generada por MiPAE, así que también se generiza (orientada, conectada).
      if (sinHallazgos) return `${nombre}.\n${basalSistema(sis.id, s.sexo)}`;
      return null;
    })
    .filter(Boolean);

  // Antecedentes del paciente (encabezado clínico) e Identificación y
  // seguridad (brazaletes/alergias) van ANTES de los sistemas — antes
  // Identificación quedaba al final del documento (la alergia enterrada).
  // Ninguno de los dos se generiza: son texto/datos del usuario.
  const encabezados = [];

  const ant = antecedentesState(s);
  const antLines = [];
  if (ant.dx) antLines.push(`Dx: ${sinPuntoFinal(ant.dx)}.`);
  if (ant.morbidos) antLines.push(`Antecedentes mórbidos: ${sinPuntoFinal(ant.morbidos)}.`);
  if (ant.quirurgicos) antLines.push(`Antecedentes quirúrgicos: ${sinPuntoFinal(ant.quirurgicos)}.`);
  if (antLines.length) encabezados.push(`Antecedentes.\n${antLines.join('\n')}`);

  const idLines = [];
  // Verificación del brazalete (chequeo de seguridad): ausente/ilegible se
  // registran con la acción tomada — el texto documenta seguridad, no culpa.
  // Sin `brazaleteEstado` (drafts viejos o solo brazo) el output es idéntico
  // al de siempre.
  if (s.brazaleteEstado === 'ausente') {
    idLines.push('Sin brazalete de identificación al recibir turno; se solicita reposición.');
  } else if (s.brazaleteEstado === 'ilegible') {
    idLines.push('Brazalete de identificación ilegible; se solicita reposición.');
  } else if (s.brazaleteBrazo) {
    idLines.push(`Brazalete de identificación en ${brazoFull(s.brazaleteBrazo)}.`);
  } else if (s.brazaleteEstado === 'presente') {
    idLines.push('Brazalete de identificación presente y legible.');
  }
  if (s.alergias?.tiene || s.alergias?.detalle?.trim()) {
    const det = s.alergias?.detalle?.trim();
    const brazo = s.alergias?.brazo ? `; brazalete de alerta en ${brazoFull(s.alergias.brazo)}` : '';
    idLines.push(`Alergias${det ? `: ${det}` : ' conocidas'}${brazo}.`);
  } else if (s.brazaleteBrazo || s.brazaleteEstado) {
    idLines.push('Sin alergias conocidas.');
  }
  // Encabezado consistente con el resto ("Identificación y seguridad." con
  // punto, como "Antecedentes." y cada sistema) — antes era el único con ":".
  if (idLines.length) encabezados.push(`Identificación y seguridad.\n${idLines.join('\n')}`);

  // Apertura ("Paciente en buen estado general, en condición estable."): la
  // primera línea del documento, antes de antecedentes y sistemas — como se
  // abre una evolución real. Sin marcar, no imprime nada (retrocompatible).
  // La procedencia se conserva en las fuentes; no se imprime en los riesgos.
  // Solo se limpia la frase de catálogo, nunca contexto clínico libre.
  const riesgosPresentados = seguridad.map(l => identificarNotaRiesgo(l)
    ? l.replace(/ \(recibido del turno anterior\)(?=\.?$)/i, '')
    : l);
  const riesgosUnicos = [...new Map(riesgosPresentados.map(l => [l.trim().replace(/\.$/, '').toLocaleLowerCase(), l.trim()])).values()];
  if (riesgosUnicos.length) encabezados.push(`Seguridad.\n${riesgosUnicos.join('\n')}`);
  const apertura = aperturaEvolucion(s);
  const bloquesFinal = [...(apertura ? [apertura] : []), ...encabezados, ...bloques]
    .map(b => {
      // Con una evolución temporal o un control posterior, conservar la
      // condición inicial en el cuerpo de la frase, no en el encabezado.
      const temporal = /\bSe recibió(?=\s|[.,;:]|$)|\bAl examen (?:f[ií]sico )?de control:/i.test(b);
      return b.replace(/\b(Al examen f[ií]sico) al recibir:/gi, temporal ? '$1: inicialmente,' : '$1:');
    });

  // El turno de la ficha vive fuera de `evolucion`. Si la autora no eligió
  // uno en el editor, usamos ese dato explícito sin modificar el borrador ni
  // inferir el turno por la hora. La elección válida del editor prevalece.
  const turnoValido = valor => Object.prototype.hasOwnProperty.call(TURNO_ENCABEZADO, valor);
  const turno = turnoValido(s.turno) ? s.turno : turnoValido(turnoDelRegistro) ? turnoDelRegistro : null;
  const fechaTxt = fecha || new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
  const encabezado = turno ? `${TURNO_ENCABEZADO[turno]}, ${fechaTxt}.` : `${upperFirst(fechaTxt)}.`;

  const cuerpo = bloquesFinal.length ? bloquesFinal.join('\n\n') : 'Sin hallazgos registrados.';
  // El registro del día ya emite turno + fecha en su cabecera; ahí se omite el
  // encabezado para no duplicar esa línea. Como módulo standalone, se incluye.
  const texto = incluirEncabezado ? [encabezado, '', cuerpo].join('\n') : cuerpo;

  return { lineas: bloquesFinal, texto };
}
